import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Layout } from "@/components/Layout";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Download,
  FileText,
  Layers,
  Loader2,
  Rocket,
  ShieldAlert,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Wrench,
  Zap,
} from "lucide-react";
import { useAuditStore } from "@/hooks/useAuditStore";
import { ROUTE_PATHS } from "@/lib";
import type { MasterResult } from "@/lib/master-engine";
import { generateAIRecommendations } from "@/lib/ai-recommendations";
import { buildActionPlanEngine } from "@/lib/action-plan-engine";
import { buildPriorityRecommendations, buildQuickWinRecommendations } from "@/lib/recommendation-builder";
import { buildSalesNarrative } from "@/lib/recommendation-engine-v2";
import { buildRecommendationMasterData } from "@/lib/build-recommendation-master-data";
import { downloadFullReport, downloadPptx } from "@/lib/fullReportGenerator";
import type { FullReportData, ReportPriorityCard, ReportQuickWin, ReportRoadmap, ReportRec } from "@/lib/fullReportGenerator";

type WebsiteAnalysis = {
  domain: string;
  title?: string;
  description?: string;
  hasLiveChat: boolean;
  hasContactForm: boolean;
  hasPricing: boolean;
  hasBlog: boolean;
  hasCaseStudies: boolean;
  hasTestimonials: boolean;
  hasDemo: boolean;
  hasFreeTrial: boolean;
  techStack: string[];
  contentQuality: "high" | "medium" | "low";
  ctaCount: number;
  mobileOptimized: boolean;
  hasSSL: boolean;
  targetAudience?: string;
  strengths?: string[];
  weaknesses?: Array<{
    key: string;
    title: string;
    severity: "low" | "medium" | "high";
    impact_on_sales?: string;
  }>;
  conversion_barriers?: string[];
  summary?: string;
  website_score?: number;
};

type MarketAnalysis = {
  companyName: string;
  domain: string;
  industry: string;
  marketPosition: "leader" | "challenger" | "niche" | "emerging";
  competitors: Array<{
    name: string;
    strengths: string[];
    weaknesses: string[];
  }>;
  marketTrends: string[];
  opportunities: string[];
  threats: string[];
  differentiators: string[];
  targetMarket: string;
  summary?: string;
};

type AIActionRecommendationResponse = {
  executive_diagnosis?: {
    headline: string;
    summary: string;
  };
  signature_sentence?: string;
  if_you_do_only_one_thing?: {
    title: string;
    text: string;
    why: string;
    consequence: string;
  };
  top_3_priorities?: Array<{
    rank: number;
    key: string;
    title: string;
    problem: string;
    business_impact: string;
    root_cause: string;
    benchmark_hint: string;
    actions: string[];
    short_term_impact: string;
    why_now?: string;
    website_link?: string;
    task_details?: Array<{
      title: string;
      description: string;
      steps: string[];
      owner: string;
      due_in_days: number;
      success_metric: string;
      effort?: string;
      impact?: string;
    }>;
  }>;
  quick_wins_14_days?: Array<{
    title: string;
    why_it_works: string;
    implementation: string;
    owner: string;
    deadline: string;
    success_metric: string;
    expected_impact?: string;
  }>;
  plan_90_days?: {
    phase_1_0_30: {
      title: string;
      goal: string;
      focus: string;
      actions: string[];
    };
    phase_2_30_60: {
      title: string;
      goal: string;
      focus: string;
      actions: string[];
    };
    phase_3_60_90: {
      title: string;
      goal: string;
      focus: string;
      actions: string[];
    };
  };
  strategic_note?: {
    headline: string;
    text: string;
  };
  phase_2_transition?: {
    headline: string;
    text: string;
  };
};

type PriorityCard = {
  rank: number;
  key: string;
  title: string;
  problem: string;
  businessImpact: string;
  rootCause: string;
  benchmarkHint: string;
  actions: string[];
  shortTermImpact: string;
  whyNow: string;
  websiteLink?: string;
  dominant?: boolean;
  // enriched fields (set by enrichedFocusFields)
  kpiContext?: string;
  ownerLabel?: string;
  revenueLeakMonthly?: number;
  taskDetails?: Array<{
    title: string;
    description: string;
    owner: string;
    dueInDays: number;
    successMetric: string;
    steps: string[];
    effort?: string;
    impact?: string;
  }>;
  enrichedActions?: string[];
};

type QuickWin = {
  title: string;
  whyItWorks: string;
  implementation: string;
  owner: string;
  deadline: string;
  successMetric: string;
  expectedImpact?: string;
};

// McKinsey Premium Design System
const card = "rounded-3xl border border-slate-200 bg-white shadow-xl";
const dominantCard =
  "rounded-3xl border-4 border-blue-600 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50 shadow-2xl relative overflow-hidden";
const soft = "rounded-2xl border border-slate-100 bg-gradient-to-br from-slate-50 to-white shadow-sm";
const pill =
  "inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white shadow-lg";

function euro(value?: number | null) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(Number(value)) ? Number(value) : 0);
}

function pct(value?: number | null, digits = 1) {
  return `${(Number.isFinite(Number(value)) ? Number(value) : 0).toFixed(digits)}%`;
}

function safeArray<T>(input: T[] | null | undefined): T[] {
  return Array.isArray(input) ? input : [];
}

function getWebsiteProofSignals(_website: WebsiteAnalysis | null) {
  // Website-Analyse deaktiviert — immer leere Signale zurückgeben
  return {
    score: 0,
    summary: "Analyse basiert auf Funnel- und Umsatzdaten.",
    biggestBarrier: "",
    barriers: [] as string[],
  };
}

function buildStructuredAiPayload(
  result: MasterResult,
  websiteAnalysis: WebsiteAnalysis | null,
  marketAnalysis: MarketAnalysis | null
) {
  const topLevers = safeArray(result.topLevers).slice(0, 3);
  const annualGap =
    (result.teamPerformance?.projectedYearEndGap || 0) > 0
      ? result.teamPerformance!.projectedYearEndGap
      : result.computedMetrics?.annualRevenueGap || result.views?.hero?.annualRevenueGap || 0;
  const monthlyGap =
    annualGap > 0
      ? Math.round(annualGap / 12)
      : result.computedMetrics?.monthlyRevenueGap || result.views?.hero?.monthlyRevenueGap || 0;
  const websiteSignals = getWebsiteProofSignals(websiteAnalysis);
  return {
    company: {
      name: result.normalizedData?.companyName || result.companyName || "Ihr Unternehmen",
      industry: result.normalizedData?.industry || "B2B",
      domain: websiteAnalysis?.domain || null,
    },
    revenue_context: {
      monthly_gap: monthlyGap,
      annual_gap: annualGap,
      current_revenue: result.computedMetrics?.currentRevenue || 0,
      potential_revenue: result.computedMetrics?.potentialRevenue || 0,
    },
    funnel_metrics: {
      contact_to_discovery_current: result.computedMetrics?.convContactToDiscovery || 0,
      contact_to_discovery_benchmark:
        result.computedMetrics?.benchmarkContactToDiscovery || 6,
      discovery_to_presentation_current:
        result.computedMetrics?.convDiscoveryToPresentation || 0,
      discovery_to_presentation_benchmark:
        result.computedMetrics?.benchmarkDiscoveryToPresentation || 50,
      presentation_to_deal_current:
        result.computedMetrics?.convPresentationToDeal || 0,
      presentation_to_deal_benchmark:
        result.computedMetrics?.benchmarkPresentationToDeal || 28,
    },
    top_levers: topLevers.map((lever, index) => ({
      rank: index + 1,
      key: `lever_${index + 1}`,
      title: lever.title,
      why_it_matters: lever.whyItMatters,
      monthly_impact: lever.monthlyImpact || 0,
      annual_impact: lever.annualImpact || 0,
    })),
    website_analysis: {
      available: !!websiteAnalysis,
      website_score: websiteSignals.score,
      summary: websiteSignals.summary,
      biggest_barrier: websiteSignals.biggestBarrier,
      barriers: websiteSignals.barriers,
      strengths: safeArray(websiteAnalysis?.strengths),
      weaknesses: safeArray(websiteAnalysis?.weaknesses).map((w) => ({
        title: w.title,
        severity: w.severity,
        impact_on_sales: w.impact_on_sales || "",
      })),
      structural_flags: {
        has_demo: !!websiteAnalysis?.hasDemo,
        has_testimonials: !!websiteAnalysis?.hasTestimonials,
        has_case_studies: !!websiteAnalysis?.hasCaseStudies,
        cta_count: websiteAnalysis?.ctaCount || 0,
      },
    },
    market_analysis: {
      available: !!marketAnalysis,
      summary:
        marketAnalysis?.summary ||
        "Keine zusätzliche Marktanalyse verfügbar oder Marktbetrachtung noch nicht geladen.",
      opportunities: safeArray(marketAnalysis?.opportunities),
      threats: safeArray(marketAnalysis?.threats),
      competitor_observations: safeArray(
        marketAnalysis?.competitors?.flatMap((c) => c.strengths.slice(0, 1)) || []
      ),
    },
    diagnostic_scoring: {
      overall_score: result.diagnosticScoring?.overallScore || 0,
      weakest_dimension: result.diagnosticScoring?.weakestDimension || null,
      strongest_dimension: result.diagnosticScoring?.strongestDimension || null,
      weakest_dimensions_ranked: result.diagnosticScoring?.weakestDimensionsRanked || [],
      priority_drivers: result.diagnosticScoring?.priorityDrivers || [],
    },
    priority_titles_from_engine: result.priorityTitles || [],
    team_performance: result.teamPerformance
      ? {
          productive_reps: result.teamPerformance.productiveReps,
          total_revenue_target_year: result.teamPerformance.totalRevenueTargetYear,
          revenue_achieved_ytd: result.teamPerformance.revenueAchievedYTD,
          expected_revenue_by_now: result.teamPerformance.expectedRevenueByNow,
          absolute_goal_attainment_pct: result.teamPerformance.absoluteGoalAttainmentPct,
          pacing_vs_plan_pct: result.teamPerformance.pacingVsPlanPct,
          pace_gap_to_date: result.teamPerformance.paceGapToDate,
          projected_year_end_revenue: result.teamPerformance.projectedYearEndRevenue,
          projected_year_end_gap: result.teamPerformance.projectedYearEndGap,
          target_per_rep_year: result.teamPerformance.targetPerRepYear,
          target_per_rep_month: result.teamPerformance.targetPerRepMonth,
          actual_revenue_per_rep_ytd: result.teamPerformance.actualRevenuePerRepYTD,
          actual_revenue_per_rep_month_paced: result.teamPerformance.actualRevenuePerRepMonthPaced,
          target_management_score: result.teamPerformance.targetManagementScore,
          pipeline_readiness_score: result.teamPerformance.pipelineReadinessScore,
          team_execution_score: result.teamPerformance.teamExecutionScore,
          rep_health_score: result.teamPerformance.repHealthScore,
          pacing_recovery_potential_monthly: result.teamPerformance.pacingRecoveryPotentialMonthly,
          rep_alignment_potential_monthly: result.teamPerformance.repAlignmentPotentialMonthly,
          pipeline_activation_potential_monthly: result.teamPerformance.pipelineActivationPotentialMonthly,
          summary_signals: result.teamPerformance.summarySignals,
        }
      : null,
    goal_path: result.computedMetrics?.goalPath
      ? {
          // --- Mathematischer Pfad (aktuelle Conversion-Raten als Basis) ---
          remaining_revenue_needed: result.computedMetrics.goalPath.remainingRevenueNeeded,
          months_remaining_approx: result.computedMetrics.goalPath.monthsRemainingApprox,
          required_monthly_revenue_from_now: result.computedMetrics.goalPath.requiredMonthlyRevenueFromNow,
          required_deals_remaining: result.computedMetrics.goalPath.requiredDealsRemaining,
          required_deals_per_month_from_now: result.computedMetrics.goalPath.requiredDealsPerMonthFromNow,
          required_presentations_remaining: result.computedMetrics.goalPath.requiredPresentationsRemaining,
          required_presentations_per_month_from_now: result.computedMetrics.goalPath.requiredPresentationsPerMonthFromNow,
          required_discovery_calls_remaining: result.computedMetrics.goalPath.requiredDiscoveryCallsRemaining,
          required_discovery_calls_per_month_from_now: result.computedMetrics.goalPath.requiredDiscoveryCallsPerMonthFromNow,
          required_contacts_remaining: result.computedMetrics.goalPath.requiredContactsRemaining,
          required_contacts_per_month_from_now: result.computedMetrics.goalPath.requiredContactsPerMonthFromNow,
          required_contact_to_discovery_pct_at_current_volume: result.computedMetrics.goalPath.requiredContactToDiscoveryPctAtCurrentVolume,
          required_discovery_to_presentation_pct_at_current_volume: result.computedMetrics.goalPath.requiredDiscoveryToPresentationPctAtCurrentVolume,
          required_presentation_to_deal_pct_at_current_volume: result.computedMetrics.goalPath.requiredPresentationToDealPctAtCurrentVolume,
          // --- Realistischer Pfad (audit-adjustierte Conversion-Raten) ---
          realistic_required_contacts_per_month: result.computedMetrics.goalPath.realisticRequiredContactsPerMonthFromNow,
          realistic_required_discovery_calls_per_month: result.computedMetrics.goalPath.realisticRequiredDiscoveryCallsPerMonthFromNow,
          realistic_required_presentations_per_month: result.computedMetrics.goalPath.realisticRequiredPresentationsPerMonthFromNow,
          realistic_required_contacts_per_rep: result.computedMetrics.goalPath.realisticRequiredContactsPerRepPerMonthFromNow,
          realistic_required_discovery_per_rep: result.computedMetrics.goalPath.realisticRequiredDiscoveryCallsPerRepPerMonthFromNow,
          realistic_required_presentations_per_rep: result.computedMetrics.goalPath.realisticRequiredPresentationsPerRepPerMonthFromNow,
          // --- Confidence ---
          confidence_score: result.computedMetrics.goalPath.confidenceScore,
          confidence_label: result.computedMetrics.goalPath.confidenceLabel,
          // --- Aktuelle Conversion-Raten ---
          adjusted_c2d: result.computedMetrics.goalPath.adjustedC2D,
          adjusted_d2p: result.computedMetrics.goalPath.adjustedD2P,
          adjusted_p2d: result.computedMetrics.goalPath.adjustedP2D,
        }
      : null,
    ownership_clarity: {
      lead_owner_defined: false,
      discovery_process_standardized: false,
      follow_up_process_defined: false,
    },
    output_rules: {
      make_priority_1_dominant: true,
      tie_website_to_sales_logic: true,
      generate_more_individuality: true,
      quick_wins_must_include_owner_deadline_kpi: true,
    },
    // ── Action Plan Engine – strukturierte Maßnahmen-Payload ──
    action_plan_engine: buildActionPlanEngine(result).aiPayload,
    // ── Rep Execution Path – operative Soll/Ist-Werte pro Rep ──
    rep_execution_path: result.repExecutionPath
      ? {
          monthly_revenue_per_rep_target: result.repExecutionPath.monthlyRevenuePerRepTarget,
          current_revenue_per_rep: result.repExecutionPath.currentRevenuePerRep,
          monthly_deals_per_rep_target: result.repExecutionPath.monthlyDealsPerRepTarget,
          current_deals_per_rep: result.repExecutionPath.currentDealsPerRep,
          monthly_contacts_per_rep_target: result.repExecutionPath.monthlyContactsPerRepTarget,
          current_contacts_per_rep: result.repExecutionPath.currentContactsPerRep,
          monthly_discovery_calls_per_rep_target: result.repExecutionPath.monthlyDiscoveryCallsPerRepTarget,
          current_discovery_calls_per_rep: result.repExecutionPath.currentDiscoveryCallsPerRep,
          monthly_presentations_per_rep_target: result.repExecutionPath.monthlyPresentationsPerRepTarget,
          current_presentations_per_rep: result.repExecutionPath.currentPresentationsPerRep,
          productive_reps: result.repExecutionPath.productiveReps,
          confidence_score: result.repExecutionPath.confidenceScore,
          confidence_label: result.repExecutionPath.confidenceLabel,
        }
      : null,
    // ── Akquise-Mix – Neukundenkanäle ──
    acquisition_mix: (() => {
      const methods = result.acquisitionMethods || {};
      const shares = Object.entries(methods)
        .map(([key, value]) => ({ channel: key, share: Number(value) || 0 }))
        .filter(s => s.share > 0)
        .sort((a, b) => b.share - a.share);
      const dominant = shares.length > 0 && shares[0].share >= 50 ? shares[0].channel : null;
      const isDiversified = shares.length >= 3 && (shares[0]?.share || 0) < 50;
      return { shares, dominant, isDiversified, channelCount: shares.length };
    })(),
    // ── Fokus-Prioritäten – Top-3 priorisierte Handlungsfelder ──
    focus_priorities: result.focusPriorities
      ? result.focusPriorities.slice(0, 3).map((fp) => ({
          rank: fp.rank,
          title: fp.title,
          summary: fp.summary,
          rationale: fp.rationale,
        }))
      : [],
  };
}

function fallbackExecutiveDiagnosis(
  result: MasterResult,
  websiteAnalysis: WebsiteAnalysis | null
) {
  const team = result.teamPerformance;
  const annualGapFallback =
    (team?.projectedYearEndGap || 0) > 0
      ? team!.projectedYearEndGap
      : result.computedMetrics?.annualRevenueGap || 0;
  const monthlyGap =
    annualGapFallback > 0
      ? Math.round(annualGapFallback / 12)
      : result.computedMetrics?.monthlyRevenueGap || 0;
  
  // NEUE LOGIK: Rep-Performance-Probleme haben absolute Priorität in der Headline
  if (team?.summarySignals) {
    const signals = team.summarySignals;
    
    // Pacing Problem ist die härteste Wahrheit
    if (signals.hasPacingProblem) {
      return {
        headline: `Ihr Team liegt aktuell unter dem linearen Sollverlauf für das Jahresziel. Bei aktueller Pace entsteht bis Jahresende eine Ziellücke von ${euro(team.projectedYearEndGap)}.`,
        summary: `Das Problem liegt nicht primär im Funnel, sondern in Zielsteuerung, Rep-Performance und Pipeline-Abdeckung. Ohne Gegensteuerung wird das Jahresziel von ${euro(team.totalRevenueTargetYear)} voraussichtlich verfehlt.`,
      };
    }
    
    // Target Breakdown Problem
    if (signals.hasTargetBreakdownProblem) {
      return {
        headline: "Ihr größter Umsatzverlust entsteht durch fehlende Zielsteuerung und unklare Verantwortlichkeit auf Rep-Ebene.",
        summary: `Das Umsatzziel ist nicht konsequent auf Reps heruntergebrochen. Das erschwert Steuerung, Forecast und Verantwortlichkeit. Dadurch bleiben aktuell rund ${euro(monthlyGap)} pro Monat unrealisiert.`,
      };
    }
    
    // Pipeline Problem
    if (signals.hasPipelineProblem) {
      return {
        headline: "Ihr größter Umsatzverlust entsteht durch ungleiche Pipeline-Abdeckung und schwache Pipeline-Qualität pro Rep.",
        summary: `Ein Teil der Reps verfügt nicht über ausreichend belastbare Pipeline zur Zielerreichung. Das ist ein strukturelles Risiko. Dadurch bleiben aktuell rund ${euro(monthlyGap)} pro Monat unrealisiert.`,
      };
    }
    
    // Variance Problem
    if (signals.hasVarianceProblem) {
      return {
        headline: "Ihr größter Umsatzverlust entsteht durch hohe Leistungsstreuung im Team und fehlende Standardisierung.",
        summary: `Die Leistungsunterschiede zwischen Top-Performern und Teamdurchschnitt sind hoch. Ein Teil des Potenzials liegt in Standardisierung und Coaching. Dadurch bleiben aktuell rund ${euro(monthlyGap)} pro Monat unrealisiert.`,
      };
    }
  }
  
  // Fallback: Dimensionen-basierte Headline
  const weakestDim = result.diagnosticScoring?.weakestDimension;
  const priorityDrivers = result.diagnosticScoring?.priorityDrivers || [];
  
  const headline = weakestDim === "REP_PERFORMANCE"
    ? "Ihr größter Umsatzverlust entsteht durch ungleiche Rep-Performance, fehlende Zielerreichung und schwache Pipeline-Qualität."
    : weakestDim === "PROCESS"
    ? "Ihr größter Umsatzverlust entsteht durch fehlende Ownership, Standards und systematische Steuerung."
    : weakestDim === "SALES_QUALITY"
    ? "Ihr größter Umsatzverlust entsteht durch unstrukturierte Discovery und zu frühe Angebote ohne echte Kaufreife."
    : weakestDim === "DEMAND"
    ? "Ihr größter Umsatzverlust entsteht durch schlechte Leadqualität und fehlende Vorqualifizierung."
    : weakestDim === "POSITIONING"
    ? "Ihr größter Umsatzverlust entsteht durch schwache Differenzierung und zu wenig sichtbaren Proof."
    : "Ihr größter Umsatzverlust entsteht nicht durch zu wenig Nachfrage, sondern durch einen unsauberen Übergang von Interesse zu Vertriebsgespräch.";
  
  const summary = priorityDrivers.length > 0
    ? `${priorityDrivers[0].summary} Dadurch bleiben aktuell rund ${euro(monthlyGap)} pro Monat unrealisiert.`
    : `Dadurch bleiben aktuell rund ${euro(monthlyGap)} pro Monat unrealisiert.`;
  
  return { headline, summary };
}

function fallbackSignatureSentence(result: MasterResult) {
  const team = result.teamPerformance;
  
  // NEUE LOGIK: Rep-Performance-Probleme haben Priorität
  if (team?.summarySignals?.hasPacingProblem) {
    return "Das Kernproblem ist nicht der Funnel, sondern fehlende Zielsteuerung, ungleiche Rep-Performance und schwache Pipeline-Abdeckung.";
  }
  
  if (team?.summarySignals?.hasTargetBreakdownProblem) {
    return "Das Kernproblem ist fehlende Zieltransparenz und unklare Verantwortlichkeit auf Rep-Ebene.";
  }
  
  return "Das Kernproblem ist kein Mangel an Marktpotenzial, sondern fehlende Geschwindigkeit, Klarheit und Verbindlichkeit an der kritischsten Stelle Ihres Funnels.";
}

function fallbackOneThing(result: MasterResult, websiteAnalysis: WebsiteAnalysis | null) {
  const team = result.teamPerformance;
  const topLever = safeArray(result.topLevers)[0];
  
  // NEUE LOGIK: Rep-Performance-Probleme haben absolute Priorität
  if (team?.summarySignals?.hasPacingProblem) {
    return {
      title: "Sales Pace auf Zielkurs bringen",
      text: "Wenn Sie nur eine Sache umsetzen, dann bringen Sie Ihr Team zurück auf Zielkurs.",
      why: `Bei aktueller Pace entsteht bis Jahresende eine Ziellücke von ${euro(team.projectedYearEndGap)}. Das Problem ist nicht der Funnel, sondern fehlende Zielsteuerung und ungleiche Rep-Performance.`,
      consequence: "Wenn Sie jetzt nicht gegensteuern, wird das Jahresziel voraussichtlich verfehlt. Jeder Monat ohne Gegensteuerung vergrößert die Ziellücke.",
    };
  }
  
  if (team?.summarySignals?.hasTargetBreakdownProblem) {
    return {
      title: "Ziele operativ auf Reps herunterbrechen",
      text: "Wenn Sie nur eine Sache umsetzen, dann brechen Sie das Jahresziel auf Quartals- und Monatsziele je Rep herunter.",
      why: "Ohne klare Rep-Ziele gibt es keine Verantwortlichkeit, keine saubere Forecast-Logik und keine belastbare Steuerung.",
      consequence: "Wenn Ziele nicht sauber auf Reps heruntergebrochen sind, bleiben alle anderen Optimierungen unter Wert.",
    };
  }
  
  if (team?.summarySignals?.hasPipelineProblem) {
    return {
      title: "Pipeline-Abdeckung pro Rep stabilisieren",
      text: "Wenn Sie nur eine Sache umsetzen, dann prüfen Sie, welche Reps genug belastbare Pipeline zur Zielerreichung haben.",
      why: "Ein Teil der Reps verfügt nicht über ausreichend belastbare Pipeline. Das ist ein strukturelles Risiko.",
      consequence: "Wenn Pipeline-Probleme nicht adressiert werden, bleiben Ziele unerreichbar – unabhängig von Funnel-Optimierungen.",
    };
  }
  
  // Fallback: topLevers
  return {
    title: topLever?.title || "Lead-zu-Erstgespräch stabilisieren",
    text:
      "Wenn Sie nur eine Sache umsetzen, dann stabilisieren Sie zuerst den Übergang von Lead zu qualifiziertem Erstgespräch.",
    why: topLever?.whyItMatters || "Hier entsteht aktuell die größte operative Leckage. Ohne klare Ownership und Prozesse bleibt die Performance instabil.",
    consequence:
      "Wenn dieser Hebel nicht zuerst stabilisiert wird, verlieren Discovery, Angebote und Follow-up einen großen Teil ihrer Wirkung.",
  };
}

function fallbackPriorities(
  result: MasterResult,
  websiteAnalysis: WebsiteAnalysis | null
): PriorityCard[] {
  const topLevers = safeArray(result.topLevers).slice(0, 3);
  const websiteSignals = getWebsiteProofSignals(websiteAnalysis);
  return topLevers.map((lever, index) => {
    const current =
      index === 0
        ? result.computedMetrics?.convContactToDiscovery || 0
        : index === 1
        ? result.computedMetrics?.convDiscoveryToPresentation || 0
        : result.computedMetrics?.convPresentationToDeal || 0;
    const benchmark =
      index === 0
        ? result.computedMetrics?.benchmarkContactToDiscovery || 6
        : index === 1
        ? result.computedMetrics?.benchmarkDiscoveryToPresentation || 50
        : result.computedMetrics?.benchmarkPresentationToDeal || 28;
    return {
      rank: index + 1,
      key: `priority_${index + 1}`,
      title: lever.title || `Priorität ${index + 1}`,
      problem:
        lever.whyItMatters ||
        "In diesem Bereich entsteht aktuell ein relevanter Teil der Umsatzleckage.",
      businessImpact: lever.monthlyImpact
        ? `${euro(lever.monthlyImpact)} potenzieller monatlicher Hebel`
        : "Direkter Hebel auf Umsatz und Conversion",
      rootCause:
        index === 0
          ? "Neue Leads haben keine harte Ownership, keine verbindliche Reaktionslogik und keinen konsequenten Übergang in den nächsten sinnvollen Schritt."
          : index === 1
          ? "Discovery und Qualifizierung sind nicht standardisiert genug, wodurch Gespräche zu oft ohne echte Reife in die Angebotsphase gehen."
          : "Deals verlieren nach Angeboten an Momentum, weil klare Next Steps und Follow-up-Standards fehlen.",
      benchmarkHint: `Aktuell ${pct(current)} vs. Referenzwert ${pct(benchmark)}.`,
      actions:
        index === 0
          ? [
              "Jeder neue Lead bekommt sofort einen klaren Owner im CRM.",
              "Ein 24h-Reaktionsstandard wird verbindlich eingeführt.",
              "Jeder Lead erhält einen dokumentierten nächsten Schritt statt offener Enden.",
            ]
          : index === 1
          ? [
              "Discovery wird mit Pflichtfragen zu Pain, Priorität, Timing und Entscheidung standardisiert.",
              "Kein Angebot ohne dokumentierte Qualifizierung.",
              "Gespräche werden stärker auf Problem und Kaufreife statt auf Features geführt.",
            ]
          : [
              "Eine feste Follow-up-Sequenz nach Demos und Angeboten wird eingeführt.",
              "Jedes Angebot endet mit einem klaren nächsten Termin oder Entscheidungsfenster.",
              "Die Pipeline wird aktiv geführt statt nur beobachtet.",
            ],
      shortTermImpact:
        index === 0
          ? "Kurzfristig mehr qualifizierte Gespräche und besserer Zufluss in den restlichen Funnel."
          : index === 1
          ? "Kurzfristig weniger Streuverlust und höherwertige Opportunities."
          : "Kurzfristig mehr Abschlüsse ohne zusätzliche Leads.",
      whyNow:
        index === 0
          ? "Weil alle nachgelagerten Optimierungen unter Wert bleiben, solange dieser Schritt instabil ist."
          : index === 1
          ? "Weil bessere Qualifizierung die Qualität jeder weiteren Maßnahme erhöht."
          : "Weil vorhandene Chancen aktuell ohne Not an Momentum verlieren.",
      websiteLink: "",
      dominant: index === 0,
    };
  });
}

function fallbackQuickWins(
  websiteAnalysis: WebsiteAnalysis | null,
  result?: MasterResult | null
): QuickWin[] {
  const hasVariance = !!result?.teamPerformance?.summarySignals?.hasVarianceProblem;
  const hasPacingProblem = !!result?.teamPerformance?.summarySignals?.hasPacingProblem;
  const hasPipelineProblem = !!result?.teamPerformance?.summarySignals?.hasPipelineProblem;

  const repWins: QuickWin[] = [
    hasVariance
      ? {
          title: "Top-Performer-Verhalten systematisieren",
          whyItWorks:
            "Wenn Top-Performer deutlich stärker als der Durchschnitt abschneiden, liegt ein direkter Hebel in der Standardisierung ihres Vorgehens.",
          implementation:
            "Analysieren Sie die stärksten Reps anhand von Call-Struktur, Qualifizierung, Follow-up und Opportunity-Führung. Überführen Sie die Muster in ein verbindliches Mini-Playbook.",
          owner: "Head of Sales",
          deadline: "innerhalb von 7 Tagen starten",
          successMetric:
            "Jeder Rep arbeitet mit derselben Discovery- und Follow-up-Struktur",
        }
      : null,
    hasPacingProblem
      ? {
          title: "Sales Pace auf Zielkurs bringen",
          whyItWorks:
            "Wenn das Team unter dem linearen Sollverlauf liegt, ist schnelles Gegensteuern entscheidend, um das Jahresziel nicht zu verfehlen.",
          implementation:
            "Führen Sie ein wöchentliches Rep-Scoreboard mit Ziel, Ist-Umsatz, Win-Rate, offener Pipeline und Zielabdeckung pro Rep ein.",
          owner: "Sales Lead / Geschäftsführung",
          deadline: "innerhalb von 3 Tagen live",
          successMetric:
            "100% der Reps werden wöchentlich gegen Ziel und Pipeline-Abdeckung reviewed",
        }
      : null,
    hasPipelineProblem
      ? {
          title: "Stagnierende Pipeline pro Rep bereinigen",
          whyItWorks:
            "Eine saubere Pipeline verbessert Forecast, Fokus und Abschlusswahrscheinlichkeit unmittelbar.",
          implementation:
            "Jeder Rep bereinigt alle Deals ohne echten Fortschritt, requalifiziert offene Opportunities und versieht jede valide Chance mit Next Step und Datum.",
          owner: "Sales Lead / zuständiger Rep",
          deadline: "innerhalb von 5 Tagen abgeschlossen",
          successMetric:
            "100% der aktiven Deals mit Next Step, Datum und realistischer Abschlusswahrscheinlichkeit",
        }
      : null,
  ].filter(Boolean) as QuickWin[];

  if (repWins.length === 3) return repWins;

  return [
    ...repWins,
    {
      title: "24h-Lead-Reaktionsregel verbindlich einführen",
      whyItWorks:
        "Mehr Geschwindigkeit im Erstkontakt erhöht direkt die Chance, dass vorhandenes Interesse in qualifizierte Gespräche übergeht.",
      implementation:
        "Ab sofort wird jeder neue Lead innerhalb von 24 Stunden kontaktiert, einem Owner zugewiesen und mit einem klaren Next Step dokumentiert.",
      owner: "Head of Sales / zuständiger Vertriebler",
      deadline: "innerhalb von 48 Stunden live",
      successMetric:
        "100% aller neuen Leads mit Owner + Erstkontakt innerhalb von 24 Stunden",
    },
    {
      title: "Discovery-Template mit Pflichtfragen ausrollen",
      whyItWorks:
        "Bessere Qualifizierung reduziert Streuverluste und erhöht die Qualität der Opportunities.",
      implementation:
        "Jedes Erstgespräch nutzt ein fixes Discovery-Template mit Pflichtfragen zu Problem, Priorität, Timing, Entscheidung und aktueller Situation.",
      owner: "Sales Lead",
      deadline: "innerhalb von 3 Tagen einführen",
      successMetric:
        "Kein Angebot ohne vollständig dokumentierte Discovery-Basis",
    },
  ].slice(0, 3);
}

function getGenericRoadmap() {
  return {
    phase_1_0_30: {
      title: "0–30 Tage",
      goal: "Die größte Leckage im oberen Funnel schließen.",
      focus: "Geschwindigkeit, Ownership und Erstkontakt-Disziplin",
      actions: [
        "Lead-Owner, SLA und Reaktionsstandard verbindlich einführen",
        "Erstkontakt und Next Step im CRM verpflichtend dokumentieren",
        "Rep-Performance-Transparenz etablieren (Ziel vs. Ist pro Rep)",
      ],
    },
    phase_2_30_60: {
      title: "30–60 Tage",
      goal: "Qualifizierung und Opportunity-Qualität stabilisieren.",
      focus: "Discovery, Bedarfstiefe und Angebotsreife",
      actions: [
        "Discovery-Template verbindlich ausrollen",
        "No-Proposal-without-Qualification-Regel einführen",
        "Top-Performer-Verhalten dokumentieren und standardisieren",
      ],
    },
    phase_3_60_90: {
      title: "60–90 Tage",
      goal: "Follow-up, Forecast und Abschlusslogik skalierbar machen.",
      focus: "Verbindlichkeit, Nachverfolgung und Steuerbarkeit",
      actions: [
        "Follow-up-Sequenzen standardisieren",
        "Pipeline-Qualität und Forecast-Transparenz erhöhen",
        "Best Practices dokumentieren und teamweit verankern",
      ],
    },
  };
}

function fallbackStrategicNote() {
  return {
    headline: "Klarheit ist jetzt da. Der Unterschied liegt ab hier in der Umsetzung.",
    text:
      "Solange die identifizierten Hebel nicht in Standards, Verantwortlichkeiten und eine saubere Vertriebsführung übersetzt werden, bleibt ein großer Teil des Potenzials Theorie.",
  };
}

function fallbackPhase2Transition() {
  return {
    headline: "Der nächste Schritt ist nicht mehr Analyse, sondern Verankerung.",
    text:
      "Jetzt geht es um klare Ownership, Discovery-Standards, Follow-up-Logik, Pipeline-Führung und die konsequente Umsetzung der priorisierten Hebel im Vertriebsalltag.",
  };
}

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
          {label}
        </span>
        <Icon className="h-5 w-5 text-blue-400" />
      </div>
      <div className="mt-3 text-2xl font-bold text-white">{value}</div>
      {sub ? <div className="mt-1 text-sm text-slate-300">{sub}</div> : null}
    </div>
  );
}

export default function ActionRecommendationsMasterAI() {
  const navigate = useNavigate();
  const location = useLocation();

  // ── ROBUSTE RESULT-INITIALISIERUNG ────────────────────────────────────────
  // Problem: persist-Middleware von Zustand hydratisiert den Store ASYNCHRON.
  // Das bedeutet: beim ersten Render nach Stripe-Redirect ist storeResult = null,
  // obwohl localStorage Daten enthält.
  // Lösung: result direkt aus localStorage lesen (synchron, sofort verfügbar),
  // zusätzlich Store-Updates via useState/useEffect abonnieren.

  // 1. Synchron aus localStorage lesen — sofort beim ersten Render verfügbar
  const [result, setResult] = useState<MasterResult | null>(() => {
    // Erst Store prüfen (falls bereits hydratisiert)
    const storeState = useAuditStore.getState();
    if (storeState.result !== null) return storeState.result as MasterResult;
    // Dann direkt localStorage lesen
    try {
      const raw = localStorage.getItem('salesforge-audit-storage');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return (parsed?.state?.result as MasterResult) ?? null;
    } catch { return null; }
  });

  const [companyProfile, setCompanyProfile] = useState<{ companyDomain?: string; companyName?: string; [key: string]: unknown } | null>(() => {
    const storeState = useAuditStore.getState();
    if (storeState.companyProfile !== null) return storeState.companyProfile;
    try {
      const raw = localStorage.getItem('salesforge-audit-storage');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.state?.companyProfile ?? null;
    } catch { return null; }
  });

  // 2. Store-Updates abonnieren — falls Store später (async) hydratisiert
  useEffect(() => {
    const unsub = useAuditStore.subscribe((state) => {
      if (state.result !== null && result === null) {
        setResult(state.result as MasterResult);
        setCompanyProfile(state.companyProfile);
      }
    });
    // Zusätzlich: einmalig nach kurzem Delay nochmal prüfen (persist-Delay-Fallback)
    const t = setTimeout(() => {
      const current = useAuditStore.getState();
      if (current.result !== null) {
        setResult(current.result as MasterResult);
        setCompanyProfile(current.companyProfile);
      } else {
        // Letzter Fallback: localStorage nochmals lesen
        try {
          const raw = localStorage.getItem('salesforge-audit-storage');
          if (raw) {
            const parsed = JSON.parse(raw);
            const r = parsed?.state?.result ?? null;
            if (r) {
              setResult(r as MasterResult);
              setCompanyProfile(parsed?.state?.companyProfile ?? null);
              // Auch in Store schreiben
              useAuditStore.setState({ result: r, companyProfile: parsed?.state?.companyProfile ?? null });
            }
          }
        } catch { /* ignore */ }
      }
    }, 300);
    return () => { unsub(); clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [websiteAnalysis, setWebsiteAnalysis] = useState<WebsiteAnalysis | null>(null);
  const [marketAnalysis, setMarketAnalysis] = useState<MarketAnalysis | null>(null);
  const [aiStructuredOutput, setAiStructuredOutput] =
    useState<AIActionRecommendationResponse | null>(null);
  const [isLoadingSignals, setIsLoadingSignals] = useState(false);
  const [isLoadingAi, setIsLoadingAi] = useState(false);

  // ── Stripe Success: Maximale Robustheit durch Pre-Checkout-Flag ──
  // Paywall.tsx setzt localStorage.salesforge_unlocked = 'true' VOR dem Checkout-Redirect.
  // Das bedeutet: Egal was mit der URL passiert — localStorage ist immer da!
  // Zusätzlich: sessionStorage, history.state, Hash-Query als weitere Fallbacks.
  const stripeSuccess = (() => {
    try {
      // Primär: localStorage (gesetzt in Paywall.tsx VOR Stripe Checkout)
      if (localStorage.getItem('salesforge_unlocked') === 'true') return true;
      // Sekundär: sessionStorage (gesetzt in index.html inline-script)
      if (sessionStorage.getItem('salesforge_stripe_success') === 'true') return true;
      // Tertiär: history.state
      if ((window.history.state as { stripeSuccess?: boolean } | null)?.stripeSuccess) return true;
      // Quaternär: Hash-Query
      const hashQuery = window.location.hash.includes('?')
        ? window.location.hash.split('?')[1]
        : '';
      const hp = new URLSearchParams(hashQuery);
      if (hp.get('stripe_success') === 'true') return true;
      // Quinar: location.state (React Router state)
      if ((location.state as { stripeSuccess?: boolean } | null)?.stripeSuccess) return true;
    } catch { /* ignore */ }
    return false;
  })();
  const [showStripeSuccess, setShowStripeSuccess] = useState(stripeSuccess);
  useEffect(() => {
    if (stripeSuccess) {
      // sessionStorage bereinigen damit Banner nicht bei jedem Reload erscheint
      try { sessionStorage.removeItem('salesforge_stripe_success'); } catch { /* ignore */ }
      // Hash-Query bereinigen
      try {
        const hash = window.location.hash;
        if (hash.includes('stripe_success')) {
          const cleanHash = hash.split('?')[0];
          window.history.replaceState({}, '', window.location.pathname + cleanHash);
        }
      } catch { /* ignore */ }
      // Banner nach 5s ausblenden
      const t = setTimeout(() => setShowStripeSuccess(false), 5000);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kein Hydration-Guard mehr — result wird synchron aus localStorage gelesen
  const typedResult = result as MasterResult | null;
  // null-safe: buildSalesNarrative hat internen null-check
  const narrative = useMemo(() => typedResult ? buildSalesNarrative(typedResult) : null, [typedResult]);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  useEffect(() => {
    if (!typedResult) return;
    // Lade-States erst hier auf true setzen — nicht beim initialen Render
    setIsLoadingSignals(true);
    setIsLoadingAi(true);
    // Sicherheits-Timeout: nach 20s IMMER Loading beenden (kein endloser Spinner)
    const safetyTimer = setTimeout(() => {
      setIsLoadingSignals(false);
      setIsLoadingAi(false);
    }, 20000);
    async function loadEverything() {
      let loadedWebsite: WebsiteAnalysis | null = null;
      let loadedMarket: MarketAnalysis | null = null;
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        if (companyProfile?.companyDomain && supabaseUrl && supabaseKey) {
          // Market-Analyse mit Timeout (max 8s)
          const marketController = new AbortController();
          const marketTimeout = setTimeout(() => marketController.abort(), 8000);
          try {
            const marketRes = await fetch(`${supabaseUrl}/functions/v1/analyze_market`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${supabaseKey}`,
              },
              body: JSON.stringify({
                domain: companyProfile.companyDomain,
                companyName: typedResult.normalizedData?.companyName,
              }),
              signal: marketController.signal,
            });
            clearTimeout(marketTimeout);
            const marketData = await marketRes.json();
            if (marketData?.success) {
              loadedMarket = marketData.analysis;
              setMarketAnalysis(marketData.analysis);
            }
          } catch {
            clearTimeout(marketTimeout);
            // Market-Analyse optional — kein Fehler
          }
        }
      } catch (error) {
        console.error("Loading website/market signals failed:", error);
      } finally {
        setIsLoadingSignals(false);
      }
      try {
        const structuredPayload = buildStructuredAiPayload(
          typedResult,
          loadedWebsite,
          loadedMarket
        );
        const aiResponse = await generateAIRecommendations(
          typedResult,
          companyProfile?.companyDomain,
          structuredPayload
        );
        if (aiResponse?.structuredOutput) {
          setAiStructuredOutput(aiResponse.structuredOutput as AIActionRecommendationResponse);
        }
      } catch (error) {
        console.error("Structured AI recommendation failed:", error);
      } finally {
        setIsLoadingAi(false);
        clearTimeout(safetyTimer);
      }
    }
    loadEverything();
    return () => clearTimeout(safetyTimer);
  }, [typedResult, companyProfile]);
  // Pace-Gap hat Priorität (Ziel vs. Forecast) — Funnel-Gap als Fallback
  const annualGap =
    (typedResult?.teamPerformance?.projectedYearEndGap || 0) > 0
      ? (typedResult?.teamPerformance?.projectedYearEndGap ?? 0)
      : typedResult?.computedMetrics?.annualRevenueGap ||
        typedResult?.views?.hero?.annualRevenueGap ||
        0;
  const monthlyGap =
    annualGap > 0
      ? Math.round(annualGap / 12)
      : typedResult?.computedMetrics?.monthlyRevenueGap ||
        typedResult?.views?.hero?.monthlyRevenueGap ||
        0;
  const websiteSignals = useMemo(
    () => getWebsiteProofSignals(websiteAnalysis),
    [websiteAnalysis]
  );
  const executiveDiagnosis = useMemo(() => {
    if (!typedResult) return null;
    return (
      aiStructuredOutput?.executive_diagnosis ||
      fallbackExecutiveDiagnosis(typedResult, websiteAnalysis)
    );
  }, [typedResult, aiStructuredOutput, websiteAnalysis]);
  const signatureSentence = useMemo(() => {
    if (!typedResult) return '';
    return aiStructuredOutput?.signature_sentence || fallbackSignatureSentence(typedResult);
  }, [aiStructuredOutput, typedResult]);
  const oneThing = useMemo(() => {
    if (!typedResult) return null;
    return (
      aiStructuredOutput?.if_you_do_only_one_thing ||
      fallbackOneThing(typedResult, websiteAnalysis)
    );
  }, [typedResult, aiStructuredOutput, websiteAnalysis]);
  const priorities = useMemo<PriorityCard[]>(() => {
    if (!typedResult) return [];
    if (safeArray(aiStructuredOutput?.top_3_priorities).length === 3) {
      return safeArray(aiStructuredOutput?.top_3_priorities).map((p, index) => ({
        rank: p.rank || index + 1,
        key: p.key || "priority_" + (index + 1),
        title: p.title,
        problem: p.problem,
        businessImpact: p.business_impact,
        rootCause: p.root_cause,
        benchmarkHint: p.benchmark_hint,
        actions: safeArray(p.actions).slice(0, 3),
        shortTermImpact: p.short_term_impact,
        whyNow:
          p.why_now ||
          (index === 0
            ? "Weil alle nachgelagerten Optimierungen unter Wert bleiben, solange dieser Schritt instabil ist."
            : "Weil dieser Hebel direkt auf die Qualität und Wirkung des restlichen Prozesses einzahlt."),
        websiteLink: "",
        dominant: index === 0,
      }));
    }
    // Fallback: Action Plan Engine – präzise, datengetrieben, mit Owner & Signal
    const enginePlan = buildActionPlanEngine(typedResult);
    return enginePlan.priorityPlans.map((p) => ({
      rank: p.rank,
      key: p.key,
      title: p.title,
      problem: p.problem,
      businessImpact: p.businessImpact,
      rootCause: p.rootCause,
      benchmarkHint: "Umsatzleck: " + new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(p.revenueLeakMonthly) + "/Monat",
      actions: p.tasks.flatMap((t) => t.steps).slice(0, 4),
      shortTermImpact: p.expectedShortTermImpact,
      whyNow: p.whyNow,
      websiteLink: "",
      dominant: p.rank === 1,
    }));
  }, [typedResult, aiStructuredOutput]);
  const quickWins = useMemo<QuickWin[]>(() => {
    if (safeArray(aiStructuredOutput?.quick_wins_14_days).length === 3) {
      return safeArray(aiStructuredOutput?.quick_wins_14_days).map((q) => ({
        title: q.title,
        whyItWorks: q.why_it_works,
        implementation: q.implementation,
        owner: q.owner,
        deadline: q.deadline,
        successMetric: q.success_metric,
        expectedImpact: q.expected_impact,
      }));
    }
    // Fallback: Action Plan Engine – mit Owner, Deadline, KPI
    if (typedResult) {
      const enginePlan = buildActionPlanEngine(typedResult);
      return enginePlan.quickWins.map((q) => ({
        title: q.title,
        whyItWorks: q.whyItWorks,
        implementation: q.implementation,
        owner: q.owner,
        deadline: q.dueInDays + " Tage",
        successMetric: q.successMetric,
      }));
    }
    return buildQuickWinRecommendations(typedResult).map(qw => ({
      title: qw?.title ?? "Quick Win",
      whyItWorks: qw?.description ?? "",
      implementation: Array.isArray(qw?.steps) ? qw.steps.join(" → ") : "",
      owner: "Sales Lead",
      deadline: qw?.timeframe ?? "14 Tage",
      successMetric: qw?.impact ? `${qw.impact} Impact in ${qw.timeframe ?? "14 Tage"}` : "",
    }));
  }, [aiStructuredOutput, websiteAnalysis, typedResult]);
  const roadmap = useMemo(() => {
    if (aiStructuredOutput?.plan_90_days) {
      return aiStructuredOutput.plan_90_days;
    }

    // Fallback: Build roadmap based on actual audit data
    if (!typedResult) return getGenericRoadmap();

    const signals = typedResult.teamPerformance?.summarySignals;
    const topLevers = typedResult.topLevers || [];
    const hasPacing = signals?.hasPacingProblem || false;
    const hasTargetBreakdown = signals?.hasTargetBreakdownProblem || false;
    const hasPipeline = signals?.hasPipelineProblem || false;
    const hasVariance = signals?.hasVarianceProblem || false;
    const hasReview = signals?.hasReviewProblem || false;

    // Phase 1: Die dringendsten Probleme (0-30 Tage)
    const phase1Actions: string[] = [];
    let phase1Goal = "Kritische Leckagen schließen und Transparenz schaffen.";
    let phase1Focus = "Schnelle Wins, Ownership und Sichtbarkeit";

    if (hasPacing) {
      phase1Goal = "Pace-Gap stoppen und monatlichen Aufholbedarf aktivieren.";
      phase1Actions.push(
        `Monatliches Pace-Review einführen: Jeder Rep kennt sein Soll (${euro(typedResult.teamPerformance.targetPerRepMonth)}/Monat) und seinen Ist-Stand.`
      );
      phase1Actions.push(
        "Wöchentliches Pipeline-Review: Welche Deals schließen diese Woche? Was blockiert?"
      );
    }

    if (hasTargetBreakdown) {
      phase1Actions.push(
        "Jahresziel auf Rep-Ebene herunterbrechen und verbindlich kommunizieren (inkl. monatlicher Meilensteine)."
      );
      phase1Actions.push(
        "Rep-Performance-Dashboard einführen: Jeder Rep sieht sein Ziel, seinen Ist-Stand und seinen Gap."
      );
    }

    if (hasPipeline) {
      phase1Actions.push(
        "Pipeline-Audit durchführen: Welche Opportunities sind realistisch? Welche sind Wunschdenken?"
      );
      phase1Actions.push(
        "Mindest-Pipeline-Coverage definieren (z.B. 3x des Quartalsziels) und pro Rep tracken."
      );
    }

    if (phase1Actions.length === 0) {
      phase1Actions.push(
        "Lead-Owner, SLA und Reaktionsstandard verbindlich einführen",
        "Erstkontakt und Next Step im CRM verpflichtend dokumentieren",
        "Rep-Performance-Transparenz etablieren (Ziel vs. Ist pro Rep)"
      );
    }

    // Phase 2: Prozesse stabilisieren (30-60 Tage)
    const phase2Actions: string[] = [];
    let phase2Goal = "Qualifizierung und Opportunity-Qualität stabilisieren.";
    let phase2Focus = "Discovery, Bedarfstiefe und Angebotsreife";

    if (hasVariance) {
      phase2Goal = "Top-Performer-Verhalten standardisieren und skalieren.";
      phase2Actions.push(
        "Top-Performer-Analyse: Was machen die besten 20% anders? (Discovery, Follow-up, Closing)"
      );
      phase2Actions.push(
        "Best Practices dokumentieren und als Standard für alle Reps ausrollen."
      );
      phase2Actions.push(
        "Peer-Learning-Sessions: Top-Performer teilen ihre Methoden mit dem Team."
      );
    }

    if (hasPipeline) {
      phase2Actions.push(
        "Discovery-Template verbindlich ausrollen: Kein Angebot ohne vollständig dokumentierte Discovery."
      );
      phase2Actions.push(
        "Qualifizierungskriterien definieren: BANT, Budget, Decision Process, Timeline."
      );
    }


    if (phase2Actions.length === 0) {
      phase2Actions.push(
        "Discovery-Template verbindlich ausrollen",
        "No-Proposal-without-Qualification-Regel einführen",
        "Top-Performer-Verhalten dokumentieren und standardisieren"
      );
    }

    // Phase 3: Skalierung und Steuerung (60-90 Tage)
    const phase3Actions: string[] = [];
    let phase3Goal = "Follow-up, Forecast und Abschlusslogik skalierbar machen.";
    let phase3Focus = "Verbindlichkeit, Nachverfolgung und Steuerbarkeit";

    if (hasReview) {
      phase3Goal = "Review-Rhythmus und Steuerungsdisziplin verankern.";
      phase3Actions.push(
        "Wöchentliches 1:1 mit jedem Rep: Pipeline-Review, Deal-Coaching, Blocker-Beseitigung."
      );
      phase3Actions.push(
        "Monatliches Team-Review: Pace vs. Plan, Top-Performer-Insights, Lessons Learned."
      );
    }

    if (hasPacing) {
      phase3Actions.push(
        `Monatliches Pace-Tracking: Ist das Team auf Kurs, um den Gap von ${euro(typedResult.teamPerformance.projectedYearEndGap)} zu schließen?`
      );
      phase3Actions.push(
        "Forecast-Transparenz erhöhen: Jeder Rep committed sich auf seine monatlichen Abschlüsse."
      );
    }

    phase3Actions.push(
      "Follow-up-Sequenzen standardisieren: Kein Deal ohne strukturierten Follow-up-Plan."
    );
    phase3Actions.push(
      "Best Practices dokumentieren und teamweit verankern: Was funktioniert, wird Standard."
    );

    return {
      phase_1_0_30: {
        title: "0–30 Tage",
        goal: phase1Goal,
        focus: phase1Focus,
        actions: phase1Actions.slice(0, 4),
      },
      phase_2_30_60: {
        title: "30–60 Tage",
        goal: phase2Goal,
        focus: phase2Focus,
        actions: phase2Actions.slice(0, 4),
      },
      phase_3_60_90: {
        title: "60–90 Tage",
        goal: phase3Goal,
        focus: phase3Focus,
        actions: phase3Actions.slice(0, 4),
      },
    };
  }, [aiStructuredOutput, typedResult]);
  const strategicNote = useMemo(() => {
    return aiStructuredOutput?.strategic_note || fallbackStrategicNote();
  }, [aiStructuredOutput]);
  const phase2Transition = useMemo(() => {
    return aiStructuredOutput?.phase_2_transition || fallbackPhase2Transition();
  }, [aiStructuredOutput]);
  const masterData = useMemo(
    () => (typedResult ? buildRecommendationMasterData(typedResult) : null),
    [typedResult]
  );

  // ── GoalPath aus computedMetrics ──
  const goalPath = typedResult?.computedMetrics?.goalPath ?? null;

  // ── Root-Cause-Cluster: Top-3 Dimensionen nach Score ──
  const rootCauseClusters = useMemo(() => {
    const drivers = typedResult?.diagnosticScoring?.weakestDimensionsRanked ?? [];
    const priorityDrivers = typedResult?.diagnosticScoring?.priorityDrivers ?? [];
    // Merge: nimm weakestDimensionsRanked, fülle mit priorityDrivers auf
    if (priorityDrivers.length >= 3) return priorityDrivers.slice(0, 3);
    return drivers.slice(0, 3).map((dim) => {
      const pd = priorityDrivers.find((p) => p.dimension === dim);
      return pd ?? { dimension: dim, title: dim, score: 0, summary: "" };
    });
  }, [typedResult]);

  // ── Alle weiteren useMemo-Hooks VOR den Guards (React Hooks Rules) ────────
  const enginePlan = useMemo(() => typedResult ? buildActionPlanEngine(typedResult) : null, [typedResult]);

  const aiTaskDetails = useMemo(() => {
    const top3 = safeArray(aiStructuredOutput?.top_3_priorities);
    return top3.map(p => safeArray(p.task_details).map((td: { title: string; description: string; owner: string; due_in_days?: number; success_metric?: string; steps?: string[]; effort?: string; impact?: string }) => ({
      title: td.title,
      description: td.description,
      owner: td.owner,
      dueInDays: td.due_in_days || 14,
      successMetric: td.success_metric,
      steps: safeArray(td.steps),
      effort: td.effort || 'medium',
      impact: td.impact || 'high',
    })));
  }, [aiStructuredOutput]);

  // focusFields als useMemo damit enrichedFocusFields sicher darauf zugreifen kann
  const focusFieldsBase = useMemo<PriorityCard[]>(() => priorities.slice(0, 3), [priorities]);

  const enrichedFocusFields = useMemo(() => {
    if (!typedResult) return focusFieldsBase;
    const metrics = typedResult.computedMetrics;
    const team = typedResult.teamPerformance;

    return focusFieldsBase.map((field, i) => {
      const enginePriority = enginePlan?.priorityPlans.find((p: { key: string }) => p.key === field.key) ||
        enginePlan?.priorityPlans[i];
      const tasks = enginePriority?.tasks || [];

      let problemDetail = field.problem;
      let changeDetail = field.rootCause;
      let targetDetail = field.shortTermImpact;
      let kpiContext = '';
      let ownerLabel = enginePriority?.owner || '';
      let revenueLeakMonthly = enginePriority?.revenueLeakMonthly || 0;

      if (field.key === 'contact_to_discovery' || field.key === 'priority_1') {
        const cur = Math.round(metrics?.convContactToDiscovery || 0);
        const bench = Math.round(metrics?.benchmarkContactToDiscovery || 6);
        const gap = bench - cur;
        if (cur > 0) {
          if (gap > 0) {
            problemDetail = `Aktuell werden nur ${cur}% Ihrer Kontakte zu qualifizierten Erstgesprächen — der Referenzwert liegt bei ${bench}%. Das bedeutet: ${gap} Prozentpunkte Leckage im oberen Funnel.`;
            kpiContext = `Aktuelle Rate: ${cur}% | Benchmark: ${bench}% | Lücke: ${gap}pp`;
          } else if (gap < -3) {
            problemDetail = `Mit ${cur}% Kontakt-zu-Erstgespräch-Rate liegt Ihr Team klar über dem B2B-Referenzwert von ${bench}%. Die Erstgespräch-Conversion ist eine Stärke — der Fokus liegt auf dem Volumen.`;
            kpiContext = `Aktuelle Rate: ${cur}% | Benchmark: ${bench}% | Vorsprung: ${Math.abs(gap)}pp`;
          } else {
            problemDetail = `Mit ${cur}% Kontakt-zu-Erstgespräch-Rate bewegen Sie sich im Bereich des B2B-Referenzwerts (${bench}%). Die Rate ist solide.`;
            kpiContext = `Aktuelle Rate: ${cur}% | Benchmark: ${bench}% | Status: zielkonform`;
          }
        }
        if (gap > 0) {
          changeDetail = 'Leads brauchen sofort einen festen Owner, eine verbindliche Reaktionszeit (max. 24h) und einen dokumentierten nächsten Schritt.';
          targetDetail = `Wenn die Rate von ${cur}% Richtung ${Math.round(bench * 0.8)}–${bench}% steigt, entstehen pro Monat deutlich mehr qualifizierte Erstgespräche.`;
        } else {
          changeDetail = 'Die Erstgespräch-Rate ist stark. Der Hebel liegt jetzt im Volumen: Mehr qualifizierte Kontakte in die Pipeline bringen.';
          targetDetail = `Ziel: Das aktuelle Niveau von ${cur}% halten und gleichzeitig das Kontaktvolumen steigern.`;
        }
      } else if (field.key === 'discovery_to_presentation' || field.key === 'priority_2') {
        const cur = Math.round(metrics?.convDiscoveryToPresentation || 0);
        const bench = Math.round(metrics?.benchmarkDiscoveryToPresentation || 50);
        const gap = bench - cur;
        if (cur > 0) {
          if (gap > 0) {
            problemDetail = `Aktuell werden ${cur}% der Erstgespräche zu Präsentationen weiterentwickelt — Referenzwert: ${bench}%. Das bedeutet: ${gap} Prozentpunkte Lücke in der Qualifizierungsphase.`;
            kpiContext = `Aktuelle Rate: ${cur}% | Benchmark: ${bench}% | Lücke: ${gap}pp`;
          } else if (gap < -5) {
            problemDetail = `Mit ${cur}% Discovery-zu-Präsentation-Rate liegt Ihr Team über dem B2B-Referenzwert von ${bench}%. Achtung: Hohe Rate kann auf fehlende Qualifizierung hindeuten — Forecast-Qualität prüfen.`;
            kpiContext = `Aktuelle Rate: ${cur}% | Benchmark: ${bench}% | Vorsprung: ${Math.abs(gap)}pp`;
          } else {
            problemDetail = `Mit ${cur}% Discovery-zu-Präsentation-Rate bewegen Sie sich im Bereich des Referenzwerts (${bench}%). Die Qualifizierungstiefe ist der entscheidende Hebel.`;
            kpiContext = `Aktuelle Rate: ${cur}% | Benchmark: ${bench}% | Status: zielkonform`;
          }
        }
        if (gap > 0) {
          changeDetail = 'Jedes Erstgespräch braucht ein verbindliches Framework: Pain, Priorität, Budget, Buying-Center, Timing.';
          targetDetail = `Wenn die Rate von ${cur}% auf ${Math.round(Math.min(bench, cur + 15))}% steigt, entstehen aus demselben Volumen mehr reife Opportunities.`;
        } else {
          changeDetail = 'Die Qualifizierungsrate ist stark. Fokus liegt auf der Qualität jeder weiterentwickelten Opportunity.';
          targetDetail = `Ziel: Das aktuelle Niveau halten und die Abschlussrate der Präsentationen steigern.`;
        }
      } else if (field.key === 'presentation_to_deal' || field.key === 'priority_3') {
        const cur = Math.round(metrics?.convPresentationToDeal || 0);
        const bench = Math.round(metrics?.benchmarkPresentationToDeal || 28);
        const gap = bench - cur;
        if (cur > 0) {
          if (gap > 0) {
            problemDetail = `Aktuell werden ${cur}% der Angebote zu Abschlüssen — Referenzwert: ${bench}%. Das bedeutet: ${gap} Prozentpunkte Verlust in der letzten Funnel-Phase.`;
            kpiContext = `Aktuelle Rate: ${cur}% | Benchmark: ${bench}% | Lücke: ${gap}pp`;
          } else if (gap < -5) {
            problemDetail = `Mit ${cur}% Angebots-zu-Abschluss-Rate liegt Ihr Team deutlich über dem B2B-Referenzwert von ${bench}%. Starker Indikator — Hebel liegt im Volumen der Angebote.`;
            kpiContext = `Aktuelle Rate: ${cur}% | Benchmark: ${bench}% | Vorsprung: ${Math.abs(gap)}pp`;
          } else {
            problemDetail = `Mit ${cur}% Angebots-zu-Abschluss-Rate bewegen Sie sich nahe am Referenzwert (${bench}%). Kleines Verbesserungspotenzial durch bessere Abschluss-Disziplin.`;
            kpiContext = `Aktuelle Rate: ${cur}% | Benchmark: ${bench}% | Status: zielkonform`;
          }
        }
        if (gap > 0) {
          changeDetail = 'Jedes Angebot braucht ein klares Entscheidungsgerüst, einen definierten Next Step und eine Follow-up-Sequenz.';
          targetDetail = `Wenn die Rate von ${cur}% auf ${Math.round(Math.min(bench, cur + 10))}% steigt, schließen aus demselben Volumen mehr Deals.`;
        } else {
          changeDetail = 'Die Abschlussrate ist stark. Hebel liegt in der Erhöhung der Angebots-Pipeline und Verkürzung des Sales Cycles.';
          targetDetail = `Ziel: Volumen der Präsentationen steigern und Sales-Cycle-Länge reduzieren.`;
        }
      } else if (field.key === 'pace_gap') {
        const pacingPct = Math.round(team?.pacingVsPlanPct || 0);
        const gapEuro = euro(team?.projectedYearEndGap || 0);
        const monthlyTarget = euro(team?.targetPerRepMonth || 0);
        problemDetail = `Das Team liegt aktuell bei ${pacingPct}% des linearen Sollverlaufs. Projizierte Ziellücke: ${gapEuro}. Monatsziel je Rep: ${monthlyTarget}.`;
        kpiContext = `Pacing: ${pacingPct}% von 100% | Projizierte Lücke: ${gapEuro}`;
        changeDetail = 'Wöchentliche Pace-Reviews je Rep, individuelle Recovery-Pläne und verbindliche Commitment-Strukturen.';
        targetDetail = `Wenn Pacing auf ≥ 95% steigt, schließt sich die Ziellücke schrittweise.`;
      } else if (field.key === 'target_breakdown') {
        const reps = team?.productiveReps || 0;
        const targetYear = euro(team?.totalRevenueTargetYear || 0);
        const perRep = euro(team?.targetPerRepYear || 0);
        problemDetail = `Das Jahresziel von ${targetYear} ist auf ${reps} Reps verteilt — rechnerisch ${perRep} je Rep/Jahr.`;
        kpiContext = `Reps: ${reps} | Jahresziel: ${targetYear} | Je Rep: ${perRep}`;
        changeDetail = 'Jahres- und Quartalsziele müssen in individuelle Rep-Ziele, Monatsscheiben und wöchentliche Aktivitätsziele übersetzt werden.';
        targetDetail = 'Wenn jeder Rep sein Wochenziel und seinen Gap kennt, entsteht Eigenmotivation statt Quartals-Überraschungen.';
      } else if (field.key === 'pipeline_coverage') {
        const pipelineScore = team?.pipelineReadinessScore || 0;
        const potential = euro(team?.pipelineActivationPotentialMonthly || 0);
        problemDetail = `Pipeline-Readiness-Score: ${Math.round(pipelineScore)}/100. Monatliches Aktivierungspotenzial: ${potential}.`;
        kpiContext = `Pipeline-Score: ${Math.round(pipelineScore)}/100 | Potenzial/Monat: ${potential}`;
        changeDetail = 'Pipeline-Audits je Rep, klare Coverage-Anforderungen (3x Quartalsziel) und konsequente Deal-Hygiene.';
        targetDetail = 'Wenn Pipeline-Qualität steigt, verbessert sich Forecast-Genauigkeit und Abschlusswahrscheinlichkeit.';
      }

      const aiTasks = aiTaskDetails[i] || [];
      const engineTasks = tasks.slice(0, 4).map((t: { title: string; description: string; owner: string; dueInDays: number; successMetric: string; steps: string[]; effort: string; impact: string }) => ({
        title: t.title, description: t.description, owner: t.owner,
        dueInDays: t.dueInDays, successMetric: t.successMetric,
        steps: t.steps, effort: t.effort, impact: t.impact,
      }));
      const taskDetails = aiTasks.length >= 2 ? aiTasks : engineTasks;
      const enrichedActions = taskDetails.length > 0
        ? taskDetails.flatMap((t: { steps: string[] }) => t.steps).filter(Boolean).slice(0, 5)
        : field.actions;

      return { ...field, problem: problemDetail, rootCause: changeDetail, shortTermImpact: targetDetail, kpiContext, ownerLabel, revenueLeakMonthly, taskDetails, enrichedActions };
    });
  }, [focusFieldsBase, typedResult, enginePlan, aiTaskDetails]);

  // ── Akquise-Mix Diagnose ──
  type AcqChannelShare = { key: string; label: string; value: number };
  type AcqChannelInsight = AcqChannelShare & { scalability: string; cycleImpact: string; conversionProfile: string; riskNote?: string };
  type AcqSourceMix = { shares: AcqChannelShare[]; dominant: AcqChannelShare | null; isDiversified: boolean; hasData: boolean; insights: AcqChannelInsight[]; riskLevel: 'high' | 'medium' | 'low' | 'unknown'; strategicNote: string };

  const acqMix = useMemo((): AcqSourceMix => {
    const methods = typedResult?.acquisitionMethods || {};
    const entries = Object.entries(methods);
    if (entries.length === 0) {
      return { shares: [], dominant: null, isDiversified: false, hasData: false, insights: [], riskLevel: 'unknown', strategicNote: '' };
    }
    const shares = entries
      .map(([key, value]) => ({ key, label: getLabelForAcqMethod(key), value: Number(value) || 0 }))
      .filter(s => s.value > 0)
      .sort((a, b) => b.value - a.value);
    const total = shares.reduce((sum, s) => sum + s.value, 0);
    const hasData = total > 0;
    const dominant = hasData && shares.length > 0 && shares[0].value >= 50 ? shares[0] : null;
    const isDiversified = hasData && shares.length >= 3 && shares[0].value < 50;
    const channelTraits: Record<string, { scalability: string; cycleImpact: string; conversionProfile: string; riskNote?: string }> = {
      phone:            { scalability: 'Mittel',      cycleImpact: 'Verkürzt Sales Cycle (direkter Dialog)', conversionProfile: 'Hoch bei qualifizierten Leads',               riskNote: 'Abhängig von Rep-Kapazität und Gesprächsqualität' },
      email:            { scalability: 'Hoch',        cycleImpact: 'Verlängert tendenziell (async)',         conversionProfile: 'Niedrig ohne starke Sequenz',               riskNote: 'Sinkende Zustellraten, hoher Wettbewerb im Postfach' },
      linkedin:         { scalability: 'Mittel',      cycleImpact: 'Neutral bis verkürzend (Warm-up)',       conversionProfile: 'Mittel — stark bei persönlicher Note',       riskNote: 'Algorithmus-Abhängigkeit, Plattformrisiko' },
      xing:             { scalability: 'Niedrig',     cycleImpact: 'Neutral',                               conversionProfile: 'Gering — schrumpfende Reichweite',          riskNote: 'Relevanz stark rückläufig im DACH-Raum' },
      referrals:        { scalability: 'Niedrig',     cycleImpact: 'Stark verkürzend (Vertrauen vorhanden)', conversionProfile: 'Sehr hoch — höchste Abschlussrate aller Kanäle', riskNote: 'Nicht skalierbar ohne aktives Empfehlungssystem' },
      events:           { scalability: 'Niedrig',     cycleImpact: 'Verkürzend (persönlicher Kontakt)',      conversionProfile: 'Mittel bis hoch',                           riskNote: 'Hoher Aufwand, saisonale Abhängigkeit' },
      contentMarketing: { scalability: 'Sehr hoch',   cycleImpact: 'Verlängernd (längere Nurture-Strecke)', conversionProfile: 'Niedrig kurzfristig, hoch langfristig',      riskNote: 'Zeitverzögert — Ergebnisse erst nach 6–18 Monaten' },
      paidAds:          { scalability: 'Sehr hoch',   cycleImpact: 'Neutral',                               conversionProfile: 'Niedrig ohne starke Landing Page',           riskNote: 'Kostenintensiv, sofort wirkungslos bei Budgetstopp' },
      coldVisits:       { scalability: 'Sehr niedrig',cycleImpact: 'Stark verkürzend',                      conversionProfile: 'Mittel — hoher Aufwand pro Lead',            riskNote: 'Extrem ressourcenintensiv, kaum skalierbar' },
      partner:          { scalability: 'Hoch',        cycleImpact: 'Verkürzend (Partnervertrauen)',          conversionProfile: 'Hoch bei passendem Partner-ICP',            riskNote: 'Abhängigkeit vom Partner-Commitment' },
      other:            { scalability: 'Unbekannt',   cycleImpact: 'Unbekannt',                             conversionProfile: 'Unbekannt' },
    };
    const insights: AcqChannelInsight[] = shares.slice(0, 4).map(s => ({ ...s, ...(channelTraits[s.key] || { scalability: 'Unbekannt', cycleImpact: 'Unbekannt', conversionProfile: 'Unbekannt' }) }));
    const outboundShare = (methods.phone || 0) + (methods.email || 0) + (methods.linkedin || 0) + (methods.coldVisits || 0);
    const inboundShare  = (methods.contentMarketing || 0) + (methods.paidAds || 0);
    const referralShare = (methods.referrals || 0) + (methods.partner || 0);
    const topShare = shares[0]?.value || 0;
    let riskLevel: 'high' | 'medium' | 'low' = 'low';
    let strategicNote = '';
    if (topShare >= 60) {
      riskLevel = 'high';
      strategicNote = `Ihr Mix hat ein strukturelles Klumpenrisiko: ${shares[0].value}% Ihrer Neukundengewinnung hängen von einem einzigen Kanal ab (${shares[0].label}). Die Handlungsempfehlung adressiert konkret, wie Sie einen zweiten belastbaren Kanal aufbauen, ohne die aktuelle Pipeline zu gefährden.`;
    } else if (outboundShare >= 70) {
      riskLevel = 'medium';
      strategicNote = `Ihr Mix ist stark outbound-lastig (${Math.round(outboundShare)}%). Wachstum ist direkt an Rep-Kapazität geknüpft — jeder neue Lead kostet aktive Arbeitszeit. Die Maßnahmen in diesem Plan zeigen, wie Sie parallel skalierbare Kanäle entwickeln, ohne das Outbound-Momentum zu verlieren.`;
    } else if (referralShare >= 40) {
      riskLevel = 'medium';
      strategicNote = `${Math.round(referralShare)}% Ihrer Neukundengewinnung laufen über Empfehlungen und Partner — Kanal-Qualität hoch, Vorhersagbarkeit gering. Der Plan enthält konkrete Schritte, um das Referral-Geschäft zu systematisieren und parallele Kanäle planbar zu machen.`;
    } else if (isDiversified && inboundShare >= 20) {
      riskLevel = 'low';
      strategicNote = `Ihr Akquise-Mix ist ausgewogen: Sie kombinieren skalierbare Inbound-Kanäle (${Math.round(inboundShare)}%) mit aktivem Outbound. Die Maßnahmen fokussieren daher auf Conversion-Optimierung je Kanal und bessere Ressourcenallokation — nicht auf Kanalausbau.`;
    } else {
      riskLevel = 'medium';
      strategicNote = `Ihr Mix zeigt eine solide Verteilung, aber ohne klaren Skalierungshebel. Die Empfehlungen zeigen, welcher Kanal für Ihr Wachstumsziel den höchsten Return on Effort liefert und wie Sie ihn priorisiert ausbauen.`;
    }
    return { shares, dominant, isDiversified, hasData, insights, riskLevel, strategicNote };
  }, [typedResult]);
  // ── KURZE LADE-PAUSE vor Fallback-Screen ─────────────────────────────────
  // Wenn result null ist, zeigen wir kurz einen Lade-Spinner — nicht sofort den Fallback.
  // Das gibt dem bootstrapStoreFromStorage() und der Store-Subscription Zeit, result zu setzen.
  const [showFallback, setShowFallback] = React.useState(false);
  useEffect(() => {
    if (result !== null) return; // Alles gut
    const t = setTimeout(() => setShowFallback(true), 800);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const wasStripeSuccess = stripeSuccess;

  if (!typedResult) {
    // Kurzen Lade-Moment zeigen bevor Fallback
    if (!showFallback) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-blue-500/30 border-t-blue-500 animate-spin" />
            <p className="text-slate-400 text-sm">Analyse wird geladen…</p>
          </div>
        </div>
      );
    }
    // Wenn nach Stripe-Redirect kein result → saubere Seite statt weiß/Fehler
    if (wasStripeSuccess) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6">
          <div className="max-w-lg text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">Zahlung erfolgreich!</h2>
            <p className="text-slate-300 mb-2">Ihre Handlungsempfehlung ist jetzt freigeschaltet.</p>
            <p className="text-slate-400 text-sm mb-8">
              Um Ihre persönliche Analyse zu sehen, führen Sie bitte das Audit in diesem Browser durch
              oder öffnen Sie die Seite im Browser, in dem Sie das Audit ausgefüllt haben.
            </p>
            <button
              onClick={() => navigate(ROUTE_PATHS.AUDIT)}
              className="rounded-2xl bg-emerald-600 px-8 py-3 font-semibold text-white hover:bg-emerald-700 transition-colors"
            >
              Audit starten
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h2 className="text-2xl font-semibold text-slate-900">Keine Daten verfügbar</h2>
          <p className="mt-2 text-slate-600">Bitte führen Sie zuerst das Audit durch.</p>
          <button
            onClick={() => navigate(ROUTE_PATHS.AUDIT)}
            className="mt-6 rounded-2xl bg-emerald-600 px-6 py-3 font-semibold text-white hover:bg-emerald-700"
          >
            Zum Audit
          </button>
        </div>
      </div>
    );
  }

  const topPriority = priorities[0];
  // focusFields alias (bereits als useMemo oben definiert)
  const focusFields = focusFieldsBase;



  // ── Gemeinsamer FullReportData-Builder ──
  const buildReportData = (): FullReportData => {
    const rPriorities: ReportPriorityCard[] = enrichedFocusFields.map((f) => ({
      rank: f.rank,
      title: f.title,
      problem: f.problem,
      rootCause: f.rootCause,
      shortTermImpact: f.shortTermImpact,
      actions: f.actions,
      kpiContext: f.kpiContext,
      revenueLeakMonthly: f.revenueLeakMonthly,
      taskDetails: f.taskDetails,
      enrichedActions: f.enrichedActions,
    }));
    const rQuickWins: ReportQuickWin[] = quickWins.map((q) => ({
      title: q.title,
      whyItWorks: q.whyItWorks,
      implementation: q.implementation,
      owner: q.owner,
      deadline: q.deadline,
      successMetric: q.successMetric,
      expectedImpact: q.expectedImpact,
    }));
    const rRoadmap: ReportRoadmap = {
      phase_1_0_30: {
        title: roadmap.phase_1_0_30.title,
        goal: roadmap.phase_1_0_30.goal,
        focus: roadmap.phase_1_0_30.focus,
        actions: roadmap.phase_1_0_30.actions,
      },
      phase_2_30_60: {
        title: roadmap.phase_2_30_60.title,
        goal: roadmap.phase_2_30_60.goal,
        focus: roadmap.phase_2_30_60.focus,
        actions: roadmap.phase_2_30_60.actions,
      },
      phase_3_60_90: {
        title: roadmap.phase_3_60_90.title,
        goal: roadmap.phase_3_60_90.goal,
        focus: roadmap.phase_3_60_90.focus,
        actions: roadmap.phase_3_60_90.actions,
      },
    };
    // Build top5Recs from buildFullRecs for PDF
    const recs = buildFullRecs({
      typedResult: typedResult!,
      aiStructuredOutput,
      enrichedFocusFields,
      quickWins,
    });
    const rTop5: ReportRec[] = recs.map((r) => ({
      rank: r.rank,
      category: r.category,
      title: r.title,
      urgencyLabel: r.urgencyLabel,
      urgencyValue: r.urgencyValue,
      timeframe: r.timeframe,
      timeframePhase: r.timeframePhase,
      headline: r.headline,
      situationText: r.situationText,
      rootCauseText: r.rootCauseText,
      consequenceText: r.consequenceText,
      kpiContext: r.kpiContext,
      measures: r.measures,
      successCriteria: r.successCriteria,
      whyNow: r.whyNow,
      impactStatement: r.impactStatement,
      firstStep: r.firstStep,
    }));
    return {
      result: typedResult!,
      priorities: rPriorities,
      quickWins: rQuickWins,
      roadmap: rRoadmap,
      executiveDiagnosis: {
        headline: executiveDiagnosis?.headline || '',
        summary: executiveDiagnosis?.summary || '',
      },
      signatureSentence: signatureSentence || '',
      top5Recs: rTop5,
    };
  };

  const handleDownloadPdf = () => {
    try {
      if (!typedResult) {
        alert('Keine Audit-Daten vorhanden. Bitte führen Sie zuerst das Audit durch.');
        return;
      }
      downloadFullReport(buildReportData());
    } catch (err) {
      console.error('PDF Download error:', err);
      alert('Download fehlgeschlagen: ' + String(err));
    }
  };

  const handleDownloadPptx = () => {
    try {
      if (!typedResult) {
        alert('Keine Audit-Daten vorhanden. Bitte führen Sie zuerst das Audit durch.');
        return;
      }
      downloadPptx(buildReportData());
    } catch (err) {
      console.error('PPTX Download error:', err);
      alert('PPTX-Download fehlgeschlagen: ' + String(err));
    }
  };

  return (
    <Layout>
    <div className="min-h-screen bg-background">

      {/* ── Stripe Erfolgs-Banner ────────────────────────────────────────── */}
      {showStripeSuccess && (
        <div className="bg-emerald-600 text-white px-4 py-3 text-center text-sm font-medium flex items-center justify-center gap-2">
          <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Zahlung erfolgreich — Ihr Zugang zur Handlungsempfehlung ist freigeschaltet.
        </div>
      )}

      <div className="mx-auto max-w-5xl px-4 py-10 md:px-8 md:py-14">

        {/* Navigation */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
          <button
            onClick={() => navigate(ROUTE_PATHS.RESULTS_MASTER)}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück zu den Ergebnissen
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleDownloadPdf}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
            >
              <FileText className="h-4 w-4 text-slate-500" />
              PDF-Report
            </button>
            <button
              onClick={handleDownloadPptx}
              className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 transition-colors shadow-sm"
            >
              <Layers className="h-4 w-4 text-blue-500" />
              PowerPoint
            </button>
            <a
              href="https://calendly.com/jbhuber10"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 transition-colors shadow-sm"
            >
              <Target className="h-4 w-4" />
              Umsetzungsgespräch buchen
            </a>
          </div>
        </div>
        {/* ══════════════════════════════════════════════════════════════
             BLOCK 1: HERO — Dringlichkeit & Executive-Diagnose
        ══════════════════════════════════════════════════════════════ */}
        <section className="mb-14">

          {/* ── HERO BLOCK — wie Landingpage ── */}
          <div className="rounded-3xl overflow-hidden shadow-2xl">

            {/* Oberer Teil: Wie Landingpage — slate-950 mit Radial-Gradient */}
            <div className="relative bg-slate-950 px-8 py-10 md:px-12 md:py-14 overflow-hidden">
              {/* Hintergrund — wie Landingpage-Hero */}
              <div className="pointer-events-none absolute inset-0 z-0">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(30,60,120,0.5),transparent)]" />
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/15 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-accent/10 rounded-full blur-3xl" />
              </div>

              {/* Status-Pill */}
              <div className="relative z-10 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/8 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-300 mb-7">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
                </span>
                Audit abgeschlossen — Handlungsbedarf identifiziert
              </div>

              {/* Headline */}
              <h1 className="relative z-10 text-4xl md:text-5xl font-black text-white leading-tight tracking-tight max-w-3xl">
                {typedResult?.normalizedData?.companyName
                  ? <>{typedResult.normalizedData.companyName}:<br className="hidden md:block" /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-emerald-400 to-accent">3 Engpässe kosten Sie jeden Monat Umsatz.</span></>
                  : <>Ihr Vertrieb hat <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-emerald-400 to-accent">3 strukturelle Engpässe.</span></>}
              </h1>
              <p className="relative z-10 mt-5 text-lg text-slate-300 leading-relaxed max-w-2xl">
                {executiveDiagnosis?.summary
                  ? executiveDiagnosis.summary
                  : "Die Lücke entsteht nicht durch mangelnde Nachfrage — sondern durch fehlende Struktur, unklare Ownership und fehlende Verbindlichkeit. Das muss sich jetzt ändern."}
              </p>

              {/* KPI-Zeile: Ziellücke + monatlicher Schaden */}
              {(annualGap > 0 || monthlyGap > 0) && (
                <div className="relative z-10 mt-8 flex flex-wrap gap-4">
                  {annualGap > 0 && (
                    <div className="rounded-2xl border border-white/15 bg-white/8 px-6 py-4 backdrop-blur-sm">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Projizierte Jahres-Lücke</div>
                      <div className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-accent to-emerald-400">{euro(annualGap)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">ungenutzte Erlöse bis Jahresende</div>
                    </div>
                  )}
                  {monthlyGap > 0 && (
                    <div className="rounded-2xl border border-white/15 bg-white/8 px-6 py-4 backdrop-blur-sm">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Verlust je Monat Verzögerung</div>
                      <div className="text-3xl font-black text-white">{euro(monthlyGap)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">solange die Engpässe offen bleiben</div>
                    </div>
                  )}
                </div>
              )}

              {/* Risiko-Banner */}
              {narrative?.risk && (
                <div className="relative z-10 mt-6 flex items-start gap-3 rounded-2xl border border-white/15 bg-white/8 px-5 py-4">
                  <ShieldAlert className="h-5 w-5 text-slate-300 shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-300 leading-relaxed">
                    <span className="font-black text-primary-foreground">Kritisch: </span>{narrative.risk}
                  </p>
                </div>
              )}
            </div>

            {/* Unterer Teil: Die 3 Engpässe als Fortschrittsbalken */}
            {focusFields.length > 0 && (
              <div className="bg-slate-900 border-t border-white/10">
                <div className="px-8 pt-7 pb-2 md:px-12">
                  <div className="flex items-center justify-between mb-5">
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Die 3 Engpässe — priorisierte Reihenfolge</span>
                    <span className="text-[10px] text-slate-500 font-medium">Fokus 1 → 2 → 3 ist nicht verhandelbar</span>
                  </div>
                  <div className="space-y-4">
                    {focusFields.map((f, i) => {
                      const cluster = rootCauseClusters[i];
                      const score = cluster && typeof cluster.score === 'number' ? cluster.score : null;
                      const barPct = score !== null ? Math.min(100, Math.round(score)) : 0;
                      const isFirst = i === 0;
                      return (
                        <div key={f.key} className="flex items-center gap-5">
                          {/* Nummer */}
                          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-black ${
                            isFirst ? 'bg-accent text-white shadow-lg shadow-accent/30' : 'bg-white/10 text-slate-400'
                          }`}>{i + 1}</div>
                          {/* Label + Bar */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className={`text-sm font-bold leading-snug ${
                                isFirst ? 'text-white' : 'text-slate-400'
                              }`}>{f.title}</span>
                              {score !== null && (
                                <span className={`text-xs font-black ml-3 shrink-0 ${
                                  score < 40 ? 'text-red-400' : score < 65 ? 'text-amber-400' : 'text-emerald-400'
                                }`}>{Math.round(score)}/100</span>
                              )}
                            </div>
                            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  score !== null && score < 40 ? 'bg-red-500' : score !== null && score < 65 ? 'bg-amber-400' : 'bg-emerald-400'
                                }`}
                                style={{ width: `${barPct}%` }}
                              />
                            </div>
                          </div>
                          {/* Abhängigkeits-Chip */}
                          <div className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${
                            isFirst
                              ? 'bg-accent/20 text-accent border border-accent/30'
                              : 'bg-white/8 text-slate-500'
                          }`}>
                            {isFirst ? 'Jetzt starten' : i === 1 ? 'Danach' : 'Zuletzt'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Startpunkt-Leiste */}
                {focusFields[0] && (
                  <div className="mt-6 mx-8 mb-7 md:mx-12 rounded-2xl border border-white/15 bg-white/8 px-5 py-4 flex items-start gap-3">
                    <Zap className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-300 leading-relaxed">
                      <span className="font-black text-primary-foreground">Startpunkt: {focusFields[0].title}. </span>
                      Ohne diesen Hebel zu stabilisieren, verpufft jede weitere Maßnahme — unabhängig von Aufwand oder Budget.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Diagnose-Strip — eleganter Abschluss */}
            <div className="bg-white border-t border-slate-100 px-8 py-6 md:px-12">
              <div className="flex items-center gap-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary shadow-md">
                  <Target className="h-4 w-4 text-primary-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-[15px] font-bold text-slate-800 leading-snug">
                    {executiveDiagnosis?.headline || "Das Ziel ist erreichbar — aber nur mit konsequenter Umsetzung in der richtigen Reihenfolge."}
                  </p>
                  {narrative?.capacityMessage && (
                    <p className="mt-1.5 text-sm text-amber-700 leading-relaxed">
                      <span className="font-semibold">Kapazitätshinweis: </span>{narrative.capacityMessage}
                    </p>
                  )}
                </div>
                <div className="shrink-0 hidden md:flex items-center gap-1.5 text-xs font-semibold text-slate-400">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  Analyse abgeschlossen
                </div>
              </div>
            </div>

          </div>
        </section>


        {/* ══════════════════════════════════════════════════════════════
             QUICK WINS BLOCK: 3 SOFORTMASSNAHMEN (erste 14 Tage)
        ══════════════════════════════════════════════════════════════ */}
        {quickWins.length > 0 && (
          <section className="mb-14">
            <div className="mb-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-emerald-700 mb-4">
                <Zap className="h-3.5 w-3.5" />
                Erste 14 Tage
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Sofortmaßnahmen — Erste 14 Tage</h2>
              <p className="mt-3 text-lg text-slate-500">
                Drei Stoßrichtungen mit sofortigem Hebeleffekt. Wenig Aufwand, direkter Bezug zu Ihrem größten Engpass.
              </p>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {quickWins.map((qw, idx) => (
                <div key={idx} className={`rounded-2xl overflow-hidden border ${
                  idx === 0
                    ? 'border-emerald-300 shadow-md ring-2 ring-emerald-200'
                    : 'border-slate-200 shadow-sm'
                } bg-white flex flex-col`}>
                  {/* Header */}
                  <div className={`px-6 py-5 border-b ${
                    idx === 0
                      ? 'bg-gradient-to-br from-emerald-600 to-emerald-500 border-emerald-400'
                      : 'bg-gradient-to-br from-slate-800 to-slate-700 border-slate-600'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-white/60">
                        Stoßrichtung {idx + 1}
                      </span>
                      {idx === 0 && (
                        <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[10px] font-bold text-white">
                          ★ Höchste Priorität
                        </span>
                      )}
                    </div>
                    <h3 className="text-base font-bold text-white leading-snug">{qw.title}</h3>
                  </div>
                  {/* Body */}
                  <div className="px-6 py-5 flex-1 space-y-4">
                    {/* Warum funktioniert das */}
                    {qw.whyItWorks && (
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Warum das jetzt zählt</div>
                        <p className="text-sm text-slate-700 leading-relaxed">{qw.whyItWorks}</p>
                      </div>
                    )}
                    {/* Erwarteter Impact */}
                    {qw.expectedImpact && (
                      <div className={`rounded-xl px-4 py-3 ${
                        idx === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-blue-50 border border-blue-200'
                      }`}>
                        <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${
                          idx === 0 ? 'text-emerald-600' : 'text-blue-600'
                        }`}>Erwarteter Effekt</div>
                        <p className={`text-sm font-semibold leading-relaxed ${
                          idx === 0 ? 'text-emerald-800' : 'text-blue-800'
                        }`}>{qw.expectedImpact}</p>
                      </div>
                    )}
                  </div>
                  {/* Footer: Erfolgskriterium */}
                  {qw.successMetric && (
                    <div className={`px-6 py-4 border-t ${
                      idx === 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-slate-50 border-slate-100'
                    }`}>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className={`h-4 w-4 shrink-0 mt-0.5 ${
                          idx === 0 ? 'text-emerald-500' : 'text-slate-400'
                        }`} />
                        <p className={`text-xs font-semibold leading-relaxed ${
                          idx === 0 ? 'text-emerald-700' : 'text-slate-600'
                        }`}>{qw.successMetric}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ══════════════════════════════════════════════════════════════
             BLOCK 2: IHRE 3 FOKUSFELDER
        ══════════════════════════════════════════════════════════════ */}
        {enrichedFocusFields.length > 0 && (
          <section className="mb-14">
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Ihre 3 Fokusfelder</h2>
              <p className="mt-3 text-lg text-slate-600">
                Wo Ihr System aktuell Umsatz verliert — und in welche Richtung sich das grundsätzlich ändern muss.
              </p>
            </div>

            <div className="space-y-6">
              {enrichedFocusFields.map((field, i) => (
                <div key={field.key} className={`rounded-3xl overflow-hidden shadow-md ${
                  i === 0 ? "ring-2 ring-blue-500 ring-offset-2" : "border border-slate-200"
                }`}>
                  {/* Header mit Grafik */}
                  <div className={`relative overflow-hidden px-8 py-7 md:px-10 ${
                    i === 0
                      ? "bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600"
                      : "bg-gradient-to-r from-slate-800 to-slate-700"
                  }`}>
                    <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/5" />
                    <div className="pointer-events-none absolute -right-2 top-8 h-24 w-24 rounded-full bg-white/5" />
                    <div className="relative flex items-start gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-white text-lg font-black">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/50 mb-1">
                          {i === 0 ? "Wichtigster Hebel" : `Fokusfeld ${i + 1}`}
                        </div>
                        <h3 className="text-xl font-bold text-white leading-snug">{field.title}</h3>
                        {field.businessImpact && (
                          <p className="mt-2 text-sm text-white/70 leading-relaxed">{field.businessImpact}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Dreispaltig */}
                  <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100 bg-white">
                    <div className="px-7 py-7">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100">
                          <div className="h-2 w-2 rounded-full bg-red-500" />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-wider text-red-600">Stand heute</span>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{field.problem}</p>
                    </div>
                    <div className="px-7 py-7">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100">
                          <div className="h-2 w-2 rounded-full bg-amber-500" />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-wider text-amber-700">Was notwendig ist</span>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{field.rootCause}</p>
                    </div>
                    <div className="px-7 py-7">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100">
                          <div className="h-2 w-2 rounded-full bg-emerald-500" />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">Messbares Zielbild</span>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{field.shortTermImpact}</p>
                    </div>
                  </div>
                  {field.whyNow && (
                    <div className="flex items-start gap-3 px-7 py-4 bg-slate-50 border-t border-slate-100">
                      <Zap className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-slate-600 leading-relaxed">
                        <span className="font-semibold text-slate-800">Warum das jetzt entscheidend ist: </span>{field.whyNow}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}


        {/* ══════════════════════════════════════════════════════════════
             AKQUISE-MIX — Strategische Kanaldiagnose
        ══════════════════════════════════════════════════════════════ */}
        {acqMix.hasData && acqMix.shares.length > 0 && (
          <section className="mb-14">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-emerald-700">
                  <Activity className="h-3.5 w-3.5" />
                  Akquise-Mix
                </div>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold border ${
                  acqMix.riskLevel === 'high'   ? 'bg-red-50 text-red-700 border-red-200' :
                  acqMix.riskLevel === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  'bg-emerald-50 text-emerald-700 border-emerald-200'
                }`}>
                  {acqMix.riskLevel === 'high' ? '⚠ Klumpenrisiko' : acqMix.riskLevel === 'medium' ? '~ Optimierungspotenzial' : '✓ Solider Mix'}
                </span>
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">Ihre Akquisekanäle — strategisch bewertet</h2>
              <p className="text-muted-foreground">
                Jeder Kanal hat ein anderes Risikoprofil, eine andere Skalierbarkeit und beeinflusst Ihren Sales Cycle unterschiedlich. Die Maßnahmen dieses Plans berücksichtigen Ihren spezifischen Mix.
              </p>
            </div>

            {/* Strategische Gesamtbewertung */}
            <div className={`rounded-xl border px-5 py-4 mb-6 ${
              acqMix.riskLevel === 'high'   ? 'border-red-200 bg-red-50/60' :
              acqMix.riskLevel === 'medium' ? 'border-amber-200 bg-amber-50/60' :
              'border-emerald-200 bg-emerald-50/60'
            }`}>
              <p className="font-semibold text-sm text-foreground mb-1">
                {acqMix.riskLevel === 'high' ? 'Strukturelles Risiko erkannt' : acqMix.riskLevel === 'medium' ? 'Strategische Einordnung' : 'Mix-Bewertung'}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">{acqMix.strategicNote}</p>
            </div>

            {/* Kanal-Karten */}
            <div className="grid gap-3 sm:grid-cols-2">
              {acqMix.insights.map((ins, idx) => {
                const isTop = idx === 0;
                const barWidth = Math.min(100, Math.max(4, ins.value));
                return (
                  <div key={ins.key} className={`rounded-xl border p-4 ${ isTop ? 'border-emerald-300 bg-emerald-50/40' : 'border-border bg-muted/20' }`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${ isTop ? 'text-emerald-700' : 'text-foreground'}`}>{ins.label}</span>
                        {isTop && <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">Hauptkanal</span>}
                      </div>
                      <span className={`text-lg font-black tabular-nums ${ isTop ? 'text-emerald-700' : 'text-muted-foreground'}`}>{ins.value}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-4">
                      <div className={`h-full rounded-full ${ isTop ? 'bg-emerald-500' : 'bg-muted-foreground/50'}`} style={{ width: `${barWidth}%` }} />
                    </div>
                    <div className="space-y-2 text-xs">
                      <div className="flex gap-2">
                        <span className="w-24 shrink-0 text-muted-foreground font-medium">Skalierbarkeit</span>
                        <span className="text-foreground font-semibold">{ins.scalability}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="w-24 shrink-0 text-muted-foreground font-medium">Sales Cycle</span>
                        <span className="text-foreground">{ins.cycleImpact}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="w-24 shrink-0 text-muted-foreground font-medium">Conversion</span>
                        <span className="text-foreground">{ins.conversionProfile}</span>
                      </div>
                      {ins.riskNote && (
                        <div className="flex gap-2 pt-1 border-t border-border/50">
                          <span className="w-24 shrink-0 text-muted-foreground font-medium">Risiko</span>
                          <span className="text-amber-700">{ins.riskNote}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Weitere Kanäle kompakt */}
            {acqMix.shares.length > 4 && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Weitere Kanäle:</p>
                <div className="flex flex-wrap gap-2">
                  {acqMix.shares.slice(4).map(s => (
                    <span key={s.key} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/30 px-3 py-1 text-xs text-muted-foreground">
                      {s.label} <span className="font-bold text-foreground">{s.value}%</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* ══════════════════════════════════════════════════════════════
             TOP 5 HANDLUNGSEMPFEHLUNGEN — GROSSER DETAILBLOCK
        ══════════════════════════════════════════════════════════════ */}
        <Top5ActionRecommendations
          typedResult={typedResult}
          aiStructuredOutput={aiStructuredOutput}
          enrichedFocusFields={enrichedFocusFields}
          quickWins={quickWins}
        />

        {/* ══════════════════════════════════════════════════════════════
             BLOCK 3: INTERAKTIVER POTENZIAL-RECHNER
        ══════════════════════════════════════════════════════════════ */}
        {typedResult?.normalizedData && typedResult?.computedMetrics && (
          <section className="mb-14">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-blue-700 mb-4">
                <Zap className="h-3.5 w-3.5" />
                Live-Simulation
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
                Was passiert, wenn Sie Ihren Funnel optimieren?
              </h2>
              <p className="mt-3 text-lg text-slate-600 leading-relaxed">
                Simulieren Sie live, wie sich Verbesserungen in Ihrem Vertrieb auf den Umsatz auswirken. Bewegen Sie die Regler — und sehen Sie sofort das Potenzial.
              </p>
            </div>
            <InteractiveConversionCalculatorAI
              normalizedData={{
                monthlyContacts: typedResult.normalizedData.monthlyContacts,
                monthlyDiscoveryCalls: typedResult.normalizedData.monthlyDiscoveryCalls,
                monthlyPresentations: typedResult.normalizedData.monthlyPresentations,
                monthlyDeals: typedResult.normalizedData.monthlyDeals,
                averageDealSize: typedResult.normalizedData.averageDealSize,
                salesCycleMonths: typedResult.normalizedData.salesCycleMonths,
              }}
              computedMetrics={{
                convContactToDiscovery: typedResult.computedMetrics.convContactToDiscovery,
                convDiscoveryToPresentation: typedResult.computedMetrics.convDiscoveryToPresentation,
                convPresentationToDeal: typedResult.computedMetrics.convPresentationToDeal,
                currentRevenue: typedResult.normalizedData.monthlyRevenue,
              }}
            />
          </section>
        )}

        {/* ══════════════════════════════════════════════════════════════
             BLOCK 4: 30/60/90 TAGE PLAN
        ══════════════════════════════════════════════════════════════ */}
        <section className="mb-14">
          <div className="mb-10">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
              Der 90-Tage-Fahrplan
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              Drei Phasen — drei klare Stoßrichtungen. Jede Phase ist Voraussetzung für die nächste. Die Logik ist bewusst sequenziell.
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            <div className="hidden md:block absolute top-[64px] left-[calc(16.67%+32px)] right-[calc(16.67%+32px)] h-1 bg-gradient-to-r from-red-300 via-orange-300 to-emerald-300 z-0 rounded-full" />
            <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                phase: roadmap.phase_1_0_30,
                label: "Phase 1",
                heading: "Größtes Problem stabilisieren",
                subline: "Mindestniveau an Input sicherstellen — Ownership und Reaktionslogik verbindlich festlegen",
                gradient: "from-red-600 to-red-500",
                lightBg: "bg-red-50",
                borderColor: "border-red-200",
                textColor: "text-red-700",
                dotColor: "bg-red-500",
                icon: <Wrench className="h-6 w-6" />,
                days: "0–30 Tage",
                number: "01",
                successHint: annualGap > 0
                  ? `Notwendig: Leckagen schließen, die ${euro(Math.round(annualGap * 0.4))} der Jahres-Lücke ausmachen.`
                  : `Notwendig: Die größte operative Leckage sofort stoppen — ohne das bleiben alle weiteren Schritte wirkungslos.`,
              },
              {
                phase: roadmap.phase_2_30_60,
                label: "Phase 2",
                heading: "Effizienz konsequent umsetzen",
                subline: "Conversion-Logik vereinheitlichen — Closing standardisieren — Engpässe gezielt auflösen",
                gradient: "from-orange-500 to-amber-500",
                lightBg: "bg-orange-50",
                borderColor: "border-orange-200",
                textColor: "text-orange-700",
                dotColor: "bg-orange-500",
                icon: <Zap className="h-6 w-6" />,
                days: "30–60 Tage",
                number: "02",
                successHint: typedResult?.teamPerformance?.summarySignals?.hasVarianceProblem
                  ? `Notwendig: Closing-Ansatz der Top-Performer auf das gesamte Team übertragen — klare Kriterien definieren, wann ein Deal abschlussreif ist.`
                  : `Notwendig: Qualifizierungsstandards verbindlich festlegen — kein Angebot ohne dokumentierte Kaufreife.`,
              },
              {
                phase: roadmap.phase_3_60_90,
                label: "Phase 3",
                heading: "System aufbauen und verankern",
                subline: "Prozesse standardisieren — Team konsistent ausrichten — Skalierbarkeit herstellen",
                gradient: "from-emerald-600 to-emerald-500",
                lightBg: "bg-emerald-50",
                borderColor: "border-emerald-200",
                textColor: "text-emerald-700",
                dotColor: "bg-emerald-500",
                icon: <Rocket className="h-6 w-6" />,
                days: "60–90 Tage",
                number: "03",
                successHint: annualGap > 0
                  ? `Notwendig: Bis Tag 90 mindestens ${euro(Math.round(annualGap * 0.3))} der Jahres-Lücke strukturell schließen — durch skalierbare Steuerung.`
                  : `Notwendig: Vertriebssteuerung und Forecast-Disziplin fest verankern — das macht das System unabhängig von Einzelpersonen.`,
              },
            ].map((item, idx) => (
              <div key={idx} className="relative z-10 flex flex-col">
                {/* Icon-Knoten */}
                <div className="flex justify-center mb-6">
                  <div className={`flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br ${item.gradient} text-white shadow-xl ring-4 ring-white`}>
                    {item.icon}
                  </div>
                </div>
                {/* Karte */}
                <div className={`flex-1 rounded-3xl border-2 ${item.borderColor} bg-white shadow-md overflow-hidden flex flex-col`}>
                  {/* Header */}
                  <div className={`${item.lightBg} px-7 py-6 border-b-2 ${item.borderColor}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-2xl font-black ${item.textColor} opacity-30`}>{item.number}</span>
                      <span className={`rounded-full px-3 py-1.5 text-xs font-black ${item.lightBg} ${item.textColor} border ${item.borderColor}`}>{item.label}</span>
                    </div>
                    <div className={`text-[11px] font-bold uppercase tracking-wider ${item.textColor} mb-2`}>{item.days}</div>
                    <h3 className="text-xl font-black text-slate-900 leading-snug">{(item as any).heading}</h3>
                    <p className={`text-sm mt-1.5 ${item.textColor} font-medium leading-snug`}>{(item as any).subline}</p>
                    {/* Phasenziel aus KI */}
                    {item.phase.goal && (
                      <div className={`mt-4 rounded-lg border ${item.borderColor} bg-white/60 px-4 py-2.5`}>
                        <div className={`text-[9px] font-bold uppercase tracking-wider ${item.textColor} mb-1`}>Phasenziel</div>
                        <p className={`text-sm font-bold ${item.textColor} leading-snug`}>{item.phase.goal}</p>
                      </div>
                    )}
                  </div>
                  {/* Strategischer Fokus aus KI */}
                  {item.phase.focus && (
                    <div className="px-7 py-5 border-b border-slate-100 bg-slate-50/50">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Strategischer Fokus dieser Phase</div>
                      <p className="text-sm text-slate-600 leading-relaxed">{item.phase.focus}</p>
                    </div>
                  )}
                  {/* Stoßrichtung aus Audit */}
                  <div className="px-7 py-5 flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Konkrete Aktionen</div>
                    <div className="space-y-3">
                      {safeArray(item.phase.actions).map((action, actionIdx) => (
                        <div key={actionIdx} className="flex items-start gap-3">
                          <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded ${item.dotColor} mt-0.5`}>
                            <span className="text-white text-[9px] font-black">{actionIdx + 1}</span>
                          </div>
                          <p className="text-sm text-slate-700 leading-relaxed">{action}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Footer: Was das für Sie bedeutet */}
                  <div className={`px-7 py-5 ${item.lightBg} border-t-2 ${item.borderColor}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-wider ${item.textColor} mb-1`}>Was das für Sie bedeutet</div>
                    <p className={`text-xs font-semibold ${item.textColor} leading-relaxed`}>{item.successHint}</p>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>

          {/* GoalPath: Management-Zielkorridor (kompakt, kein operativer Detailpfad) */}
          {goalPath && goalPath.realisticRequiredContactsPerMonthFromNow > 0 && (
            <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 px-7 py-6">
              <div className="flex items-center gap-3 mb-4">
                <Target className="h-5 w-5 text-slate-500" />
                <span className="text-sm font-bold text-slate-900">Zielkorridor für die nächsten 90 Tage</span>
              </div>
              <div className="grid gap-4 md:grid-cols-3 text-sm">
                <div className="rounded-xl border border-slate-200 bg-white px-5 py-4">
                  <div className="text-xs text-muted-foreground mb-1">Benötigter Monatsumsatz</div>
                  <div className="text-xl font-bold text-foreground">{euro(goalPath.requiredMonthlyRevenueFromNow)}</div>
                  <div className="text-xs text-muted-foreground mt-1">ab sofort, um Jahresziel zu erreichen</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-5 py-4">
                  <div className="text-xs text-muted-foreground mb-1">Deals / Monat nötig</div>
                  <div className="text-xl font-bold text-foreground">{goalPath.requiredDealsPerMonthFromNow.toFixed(1)}</div>
                  <div className="text-xs text-muted-foreground mt-1">Gesamt-Team, auf Basis Ø Deal-Größe</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-5 py-4">
                  <div className="text-xs text-muted-foreground mb-1">Noch offen bis Jahresende</div>
                  <div className="text-xl font-bold text-destructive">{euro(goalPath.remainingRevenueNeeded)}</div>
                  <div className="text-xs text-muted-foreground mt-1">bei unveränderter Pace</div>
                </div>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Die detaillierte Aktivitätsplanung je Rep (Kontakte, Discovery Calls, Präsentationen) ist Bestandteil des Umsetzungsgesprächs — dort werden Ziele verbindlich auf individuelle Reps heruntergebrochen.
              </p>
            </div>
          )}

          {/* ALTES GoalPath-Detail entfernt — zu operativ für Plan-Ebene */}
          {false && goalPath && goalPath.realisticRequiredContactsPerMonthFromNow > 0 && (
            <div className="mt-8 rounded-3xl overflow-hidden border-2 border-blue-200 shadow-md">
              <div className="bg-gradient-to-r from-blue-700 to-indigo-600 px-8 py-7">
                <div className="flex items-center gap-3 mb-2">
                  <Target className="h-5 w-5 text-white" />
                  <span className="text-xs font-bold uppercase tracking-wider text-white/80">Was jetzt passieren muss, um Ihr Ziel zu erreichen</span>
                </div>
                <p className="text-sm text-blue-100 leading-relaxed">
                  Operativ belastbarer Zielpfad — audit-adjustiert, berücksichtigt operative Reibung aus Ihrem Audit. Basis für alle Aktivitätsziele.
                </p>
              </div>
              {/* Team-Gesamt + je Rep nebeneinander */}
              <div className="bg-white">
                <div className="grid grid-cols-2 border-b border-blue-100">
                  <div className="px-6 py-4 text-center border-r border-blue-100">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Gesamt-Team / Monat</div>
                  </div>
                  <div className="px-6 py-4 text-center">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Je Rep / Monat</div>
                  </div>
                </div>
                {[
                  {
                    label: "Kontakte",
                    team: Math.round(goalPath.realisticRequiredContactsPerMonthFromNow),
                    rep: Math.round(goalPath.realisticRequiredContactsPerRepPerMonthFromNow),
                    color: "text-blue-700", bg: "bg-blue-50",
                  },
                  {
                    label: "Discovery Calls",
                    team: Math.round(goalPath.realisticRequiredDiscoveryCallsPerMonthFromNow),
                    rep: Math.round(goalPath.realisticRequiredDiscoveryCallsPerRepPerMonthFromNow),
                    color: "text-indigo-700", bg: "bg-indigo-50",
                  },
                  {
                    label: "Präsentationen",
                    team: Math.round(goalPath.realisticRequiredPresentationsPerMonthFromNow),
                    rep: Math.round(goalPath.realisticRequiredPresentationsPerRepPerMonthFromNow),
                    color: "text-violet-700", bg: "bg-violet-50",
                  },
                  {
                    label: "Deals",
                    team: goalPath.requiredDealsPerMonthFromNow,
                    rep: goalPath.requiredDealsPerRepPerMonthFromNow,
                    color: "text-emerald-700", bg: "bg-emerald-50",
                    isDecimal: true,
                  },
                ].map((kpi, i) => (
                  <div key={i} className={`grid grid-cols-2 ${i < 3 ? 'border-b border-blue-50' : ''}`}>
                    <div className={`flex items-center justify-between px-6 py-5 border-r border-blue-100 ${kpi.bg}`}>
                      <span className={`text-xs font-semibold ${kpi.color} opacity-70`}>{kpi.label}</span>
                      <span className={`text-3xl font-black ${kpi.color}`}>
                        {kpi.isDecimal ? kpi.team.toFixed(1) : kpi.team}
                      </span>
                    </div>
                    <div className={`flex items-center justify-between px-6 py-5 ${kpi.bg}`}>
                      <span className={`text-xs font-semibold ${kpi.color} opacity-70`}>{kpi.label}</span>
                      <span className={`text-3xl font-black ${kpi.color}`}>
                        {kpi.isDecimal ? kpi.rep.toFixed(1) : kpi.rep}
                      </span>
                    </div>
                  </div>
                ))}
                <div className="px-6 py-4 bg-slate-50 border-t border-blue-100">
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Basis: audit-adjustierte Conversion-Raten — berücksichtigt Follow-up-Stärke, Entscheider-Einbindung, Discovery-Qualität und Pipeline-Hygiene.
                    {goalPath.usesFallbackBenchmarks ? " Teile basieren auf Benchmark-Annahmen (25% C2D · 65% D2P · 35% P2D)" : ""}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sales-Cycle-Warnung (nur wenn eigener Kontext, nicht Dopplung zum Risk-Banner) */}
          {narrative?.salesCycleWarning && (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/80 px-7 py-5 flex items-start gap-4">
              <Clock3 className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 leading-relaxed">{narrative.salesCycleWarning}</p>
            </div>
          )}
        </section>

        {/* ══════════════════════════════════════════════════════════════
             BLOCK 4b: REP EXECUTION — Aktivitätsziele je Rep
        ══════════════════════════════════════════════════════════════ */}
        {typedResult?.repExecutionPath && typedResult.repExecutionPath.monthlyDealsPerRepTarget > 0 && (
          <section className="mb-14">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-blue-700 mb-4">
                <Users className="h-3.5 w-3.5" />
                Rep-Ebene
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
                Was jeder Rep konkret leisten muss
              </h2>
              <p className="mt-3 text-lg text-slate-600 leading-relaxed">
                Auf Basis Ihres Audits: die Mindestanforderungen je Vertriebsmitarbeiter, damit das Jahresziel rechnerisch erreichbar bleibt.
              </p>
            </div>

            <div className="rounded-3xl overflow-hidden border border-blue-200 shadow-md">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-700 to-indigo-600 px-8 py-6 md:px-10">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1">Soll vs. Ist — je Rep / Monat</div>
                    <p className="text-white/80 text-sm">
                      {typedResult.repExecutionPath.productiveReps} aktive Reps · Konfidenz: <span className="font-bold text-white">{typedResult.repExecutionPath.confidenceLabel || 'Mittel'}</span>
                    </p>
                  </div>
                  <div className="rounded-xl border border-white/20 bg-white/10 px-5 py-3 text-center">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-0.5">Ziel-Umsatz / Rep / Monat</div>
                    <div className="text-2xl font-black text-white">{euro(typedResult.repExecutionPath.monthlyRevenuePerRepTarget)}</div>
                    <div className="text-xs text-white/50 mt-0.5">Ist: {euro(typedResult.repExecutionPath.currentRevenuePerRep)}</div>
                  </div>
                </div>
              </div>

              {/* KPI Grid: Soll vs. Ist */}
              <div className="bg-white">
                <div className="grid grid-cols-3 border-b border-slate-100">
                  <div className="px-6 py-3 text-center"><span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Aktivität</span></div>
                  <div className="px-6 py-3 text-center border-x border-slate-100"><span className="text-[10px] font-bold uppercase tracking-wider text-red-500">Ist / Monat</span></div>
                  <div className="px-6 py-3 text-center"><span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Soll / Monat</span></div>
                </div>
                {[
                  {
                    label: 'Kontakte',
                    ist: Math.round(typedResult.repExecutionPath.currentContactsPerRep),
                    soll: Math.round(typedResult.repExecutionPath.monthlyContactsPerRepTarget),
                  },
                  {
                    label: 'Discovery Calls',
                    ist: Math.round(typedResult.repExecutionPath.currentDiscoveryCallsPerRep),
                    soll: Math.round(typedResult.repExecutionPath.monthlyDiscoveryCallsPerRepTarget),
                  },
                  {
                    label: 'Präsentationen',
                    ist: Math.round(typedResult.repExecutionPath.currentPresentationsPerRep),
                    soll: Math.round(typedResult.repExecutionPath.monthlyPresentationsPerRepTarget),
                  },
                  {
                    label: 'Abschlüsse',
                    ist: Number(typedResult.repExecutionPath.currentDealsPerRep.toFixed(1)),
                    soll: Number(typedResult.repExecutionPath.monthlyDealsPerRepTarget.toFixed(1)),
                    isDecimal: true,
                  },
                ].map((row, i, arr) => {
                  const gap = row.soll - row.ist;
                  const isLast = i === arr.length - 1;
                  return (
                    <div key={i} className={`grid grid-cols-3 ${!isLast ? 'border-b border-slate-100' : ''}`}>
                      <div className="px-6 py-5 flex items-center">
                        <span className="text-sm font-semibold text-slate-700">{row.label}</span>
                      </div>
                      <div className="px-6 py-5 text-center border-x border-slate-100">
                        <span className={`text-2xl font-black ${ gap > 0.05 ? 'text-red-600' : gap < -0.05 ? 'text-emerald-600' : 'text-slate-700'}`}>
                          {row.isDecimal ? row.ist.toFixed(1) : row.ist}
                        </span>
                        {gap > 0.05 && (
                          <div className="text-[10px] text-red-500 font-semibold mt-0.5">
                            –{row.isDecimal ? gap.toFixed(1) : Math.ceil(gap)} unter Soll
                          </div>
                        )}
                        {gap < -0.05 && (
                          <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">
                            +{row.isDecimal ? Math.abs(gap).toFixed(1) : Math.floor(Math.abs(gap))} über Ziel
                          </div>
                        )}
                        {Math.abs(gap) <= 0.05 && (
                          <div className="text-[10px] text-slate-400 font-semibold mt-0.5">
                            Ziel erreicht
                          </div>
                        )}
                      </div>
                      <div className="px-6 py-5 text-center bg-emerald-50/50">
                        <span className="text-2xl font-black text-emerald-700">
                          {row.isDecimal ? row.soll.toFixed(1) : row.soll}
                        </span>
                        <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">Zielwert</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 space-y-2">
                <p className="text-xs text-slate-600 leading-relaxed font-medium">
                  📐 <strong>Rechenlogik (Reverse-Funnel):</strong> Startpunkt ist das monatliche Umsatzziel je Rep.
                  Daraus werden rückwärts die benötigten Abschlüsse, Präsentationen, Discovery Calls und Kontakte berechnet —
                  jeweils auf Basis {typedResult.repExecutionPath.usesFallbackBenchmarks
                    ? 'realistischer B2B-Branchen-Benchmarks (C2D: ~6 %, D2P: ~50 %, P2D: ~28 %), da keine eigenen Raten vorliegen.'
                    : 'Ihrer tatsächlichen Conversion-Raten aus dem Audit.'}
                </p>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Diese Werte sind Mindeststandards — nicht Decken. Die individuelle Zielvereinbarung je Rep erfolgt im Umsetzungsgespräch.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Loading Indicator */}
        {(isLoadingSignals || isLoadingAi) && (
          <div className="mb-6 rounded-2xl border border-blue-100 bg-blue-50/60 p-4 flex items-center gap-3 text-sm text-blue-700">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            Individuelle Analyse wird ergänzt...
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
             BLOCK 5: ÜBERGANG ZUR UMSETZUNG (CTA)
        ══════════════════════════════════════════════════════════════ */}
        <section className="mt-6">
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <div className="bg-slate-950 px-8 py-14 md:px-14 md:py-20 text-center relative overflow-hidden">
              {/* Hintergrund wie Landingpage Final-CTA */}
              <div className="pointer-events-none absolute inset-0 z-0">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_100%,rgba(34,197,94,0.1),transparent)]" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
              </div>
              <div className="relative z-10 max-w-2xl mx-auto">
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/20 border border-emerald-400/30 px-4 py-2 text-xs font-bold uppercase tracking-wider text-emerald-300 mb-7">
                  <Rocket className="h-4 w-4" />
                  Nächster Schritt
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-white leading-tight">
                  Sie sehen jetzt klar, was notwendig ist.
                </h2>
                <p className="mt-3 text-xl font-semibold text-blue-200 leading-snug">
                  Die eigentliche Herausforderung liegt nicht im Wissen — sondern in der konsequenten Umsetzung.
                </p>
                <p className="mt-5 text-base text-slate-300 max-w-xl mx-auto leading-relaxed">
                  Dieser Plan definiert die Richtung. Die konsequente Umsetzung — mit verbindlichen Verantwortlichkeiten, laufender Steuerung und einem System, das funktioniert — ist eine andere Aufgabe. Die meisten Teams scheitern nicht am Wissen. Sie scheitern an der Umsetzungsdisziplin.
                </p>

                {/* Wahrheits-Box */}
                <div className="mt-8 rounded-2xl border border-white/15 bg-white/5 px-6 py-6 text-left">
                  <div className="text-xs font-bold uppercase tracking-wider text-white/50 mb-4">Was ein Umsetzungsgespräch konkret liefert</div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {[
                      { icon: <Target className="h-4 w-4" />, text: "Den einen Hebel identifizieren, der den größten Soforteffekt hat — und alles andere zurückstellen" },
                      { icon: <CheckCircle2 className="h-4 w-4" />, text: "Verantwortlichkeiten klar zuweisen — nicht als Empfehlung, sondern als verbindliche Entscheidung" },
                      { icon: <Zap className="h-4 w-4" />, text: "Ersten Schritt für die kommenden 7 Tage festlegen — konkret, umsetzbar, messbar" },
                      { icon: <ShieldAlert className="h-4 w-4" />, text: "Die typischen Umsetzungsfehler in diesem Engpass kennen — und von Anfang an vermeiden" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="text-emerald-400 shrink-0 mt-0.5">{item.icon}</div>
                        <p className="text-sm text-slate-200 leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {annualGap > 0 && (
                  <div className="mt-6 rounded-xl border border-red-400/30 bg-red-500/10 px-5 py-4">
                    <p className="text-sm text-red-200 leading-relaxed">
                      <span className="font-black text-red-300">Jeder Monat ohne Umsetzung kostet Sie {euro(monthlyGap)}. </span>
                      Dieser Plan ist bereit. Die Frage ist, ob Sie ihn alleine umsetzen wollen.
                    </p>
                  </div>
                )}

                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://calendly.com/jbhuber10"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 hover:bg-emerald-400 px-10 py-4 text-base font-black text-white transition-colors shadow-xl"
                  >
                    <Target className="h-5 w-5" />
                    Umsetzungsgespräch buchen — kostenlos
                  </a>
                  <button
                    onClick={handleDownloadPdf}
                    className="inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 hover:bg-white/20 px-8 py-4 text-base font-semibold text-white transition-colors"
                  >
                    <Download className="h-5 w-5" />
                    Plan als HTML sichern
                  </button>
                </div>
                <p className="mt-5 text-sm text-slate-400">
                  30 Minuten. Kein Pitch. Konkreter erster Schritt.
                </p>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
    </Layout>
  );
}

// ─── Akquise-Mix Hilfsfunktion ──────────────────────────────────────────────
function getLabelForAcqMethod(key: string): string {
  const labels: Record<string, string> = {
    phone: 'Telefon', email: 'E-Mail', linkedin: 'LinkedIn', xing: 'Xing',
    referrals: 'Empfehlungen', events: 'Events & Messen', contentMarketing: 'Content Marketing',
    paidAds: 'Paid Ads', coldVisits: 'Kaltbesuche', partner: 'Partner / Channel',
    other: 'Sonstiges',
  };
  return labels[key] || key;
}

// ─── Interactive Conversion Calculator (Plan-Version) ───────────────────────
interface ICalcProps {
  normalizedData: {
    monthlyContacts: number;
    monthlyDiscoveryCalls: number;
    monthlyPresentations: number;
    monthlyDeals: number;
    averageDealSize: number;
    salesCycleMonths: number;
  };
  computedMetrics: {
    convContactToDiscovery: number;
    convDiscoveryToPresentation: number;
    convPresentationToDeal: number;
    currentRevenue: number;
  };
}

function InteractiveConversionCalculatorAI({ normalizedData, computedMetrics }: ICalcProps) {
  const [contactBoost, setContactBoost] = useState(0);
  const [discoveryBoost, setDiscoveryBoost] = useState(0);
  const [presentationBoost, setPresentationBoost] = useState(0);
  const [cycleReduction, setCycleReduction] = useState(0);
  const [dealSizeIncrease, setDealSizeIncrease] = useState(0);

  const simulated = useMemo(() => {
    const c2d = Math.min(1, (computedMetrics.convContactToDiscovery || 0) / 100 + contactBoost / 100);
    const d2p = Math.min(1, (computedMetrics.convDiscoveryToPresentation || 0) / 100 + discoveryBoost / 100);
    const p2w = Math.min(1, (computedMetrics.convPresentationToDeal || 0) / 100 + presentationBoost / 100);
    const contacts = normalizedData.monthlyContacts || 0;
    const newDisc = contacts * c2d;
    const newPres = newDisc * d2p;
    const newDeals = newPres * p2w;
    const newDeal = (normalizedData.averageDealSize || 0) * (1 + dealSizeIncrease / 100);
    const sc = normalizedData.salesCycleMonths || 3;
    const newSc = Math.max(0.5, sc * (1 - cycleReduction / 100));
    const scMult = sc > 0 ? sc / newSc : 1;
    const newRev = newDeals * scMult * newDeal;
    const delta = newRev - (computedMetrics.currentRevenue || 0);
    const pct = (computedMetrics.currentRevenue || 0) > 0 ? (delta / (computedMetrics.currentRevenue || 1)) * 100 : 0;
    return { newRev, delta, pct, newDisc, newPres, newDeals: newDeals * scMult, newDeal, newSc, scMult, c2d: c2d * 100, d2p: d2p * 100, p2w: p2w * 100 };
  }, [contactBoost, discoveryBoost, presentationBoost, cycleReduction, dealSizeIncrease, normalizedData, computedMetrics]);

  const reset = () => { setContactBoost(0); setDiscoveryBoost(0); setPresentationBoost(0); setCycleReduction(0); setDealSizeIncrease(0); };
  const fmt = (v: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  const num = (v: number, d = 1) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d }).format(v);

  const sliders = [
    { label: 'Kontakt → Discovery', sub: `${num(computedMetrics.convContactToDiscovery, 1)}% → ${num(simulated.c2d, 1)}%`, val: contactBoost, set: setContactBoost, min: 0, max: 20, step: 1, suffix: ' PP', sign: '+' },
    { label: 'Discovery → Präsentation', sub: `${num(computedMetrics.convDiscoveryToPresentation, 1)}% → ${num(simulated.d2p, 1)}%`, val: discoveryBoost, set: setDiscoveryBoost, min: 0, max: 20, step: 1, suffix: ' PP', sign: '+' },
    { label: 'Präsentation → Abschluss', sub: `${num(computedMetrics.convPresentationToDeal, 1)}% → ${num(simulated.p2w, 1)}%`, val: presentationBoost, set: setPresentationBoost, min: 0, max: 20, step: 1, suffix: ' PP', sign: '+' },
    { label: 'Sales Cycle Verkürzung', sub: `${num(normalizedData.salesCycleMonths, 1)} → ${num(simulated.newSc, 1)} Monate`, val: cycleReduction, set: setCycleReduction, min: 0, max: 50, step: 5, suffix: '%', sign: '-' },
    { label: 'Durchschnittliche Deal-Größe', sub: `${fmt(normalizedData.averageDealSize)} → ${fmt(simulated.newDeal)}`, val: dealSizeIncrease, set: setDealSizeIncrease, min: 0, max: 50, step: 5, suffix: '%', sign: '+' },
  ];

  return (
    <div className="rounded-3xl border border-blue-200 bg-white shadow-md overflow-hidden">
      {/* Result Bar */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-600 px-8 py-7">
        <div className="grid gap-6 md:grid-cols-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1">Simulierter Monatsumsatz</div>
            <div className="text-3xl font-black text-white">{fmt(simulated.newRev)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1">Zusätzlicher Umsatz / Monat</div>
            <div className={`text-3xl font-black ${simulated.delta >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
              {simulated.delta >= 0 ? '+' : ''}{fmt(simulated.delta)}
            </div>
            <div className="text-xs text-white/50 mt-1">{fmt(simulated.delta * 12)} p.a. Jahrespotenzial</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1">Steigerung</div>
            <div className={`text-3xl font-black ${simulated.pct >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
              {simulated.pct >= 0 ? '+' : ''}{num(simulated.pct, 1)}%
            </div>
          </div>
        </div>
        {cycleReduction > 0 && (
          <div className="mt-5 pt-5 border-t border-white/20 text-sm text-white/80">
            <span className="font-semibold text-white">Sales Cycle Boost: </span>
            {num(simulated.scMult, 2)}× mehr Abschlüsse pro Monat durch kürzeren Zyklus
          </div>
        )}
      </div>

      {/* Sliders */}
      <div className="px-8 py-7 space-y-7">
        {sliders.map((s) => (
          <div key={s.label}>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-slate-800">{s.label}</label>
              <span className="text-sm text-slate-500">{s.sub}</span>
            </div>
            <input
              type="range"
              min={s.min}
              max={s.max}
              step={s.step}
              value={s.val}
              onChange={(e) => s.set(Number(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>{s.sign}0{s.suffix}</span>
              <span className="font-semibold text-blue-600">{s.sign}{s.val}{s.suffix}</span>
              <span>{s.sign}{s.max}{s.suffix}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Funnel Comparison Table */}
      <div className="mx-8 mb-7 rounded-2xl border border-blue-100 overflow-hidden">
        <div className="grid grid-cols-3 bg-blue-50 px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          <div>Funnel-Stufe</div>
          <div className="text-center">Aktuell / Monat</div>
          <div className="text-center text-blue-600">Simuliert / Monat</div>
        </div>
        {[
          { label: 'Kontakte', ist: normalizedData.monthlyContacts, sim: normalizedData.monthlyContacts },
          { label: 'Discovery Calls', ist: normalizedData.monthlyDiscoveryCalls, sim: simulated.newDisc },
          { label: 'Präsentationen', ist: normalizedData.monthlyPresentations, sim: simulated.newPres },
          { label: 'Abschlüsse', ist: normalizedData.monthlyDeals, sim: simulated.newDeals, dec: 1 },
        ].map((r, i, arr) => (
          <div key={r.label} className={`grid grid-cols-3 px-5 py-4 text-sm ${i < arr.length - 1 ? 'border-b border-blue-50' : ''}`}>
            <span className="font-semibold text-slate-700">{r.label}</span>
            <span className="text-center text-slate-500">{num(r.ist, r.dec ?? 0)}</span>
            <span className={`text-center font-bold ${r.sim > r.ist ? 'text-emerald-600' : r.sim < r.ist ? 'text-red-500' : 'text-slate-500'}`}>
              {num(r.sim, r.dec ?? 0)}
            </span>
          </div>
        ))}
      </div>

      {/* Reset */}
      <div className="px-8 pb-7">
        <button
          onClick={reset}
          className="w-full py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold transition-colors"
        >
          Alle Regler zurücksetzen
        </button>
        <p className="mt-3 text-center text-xs text-slate-400">
          Simulation auf Basis Ihrer Audit-Daten. Kein Ersatz für eine individuelle Beratung.
        </p>
      </div>
    </div>
  );
}

// ─── TOP 5 HANDLUNGSEMPFEHLUNGEN — Premium Detailblock ──────────────────────
interface Top5Props {
  typedResult: MasterResult | null;
  aiStructuredOutput: AIActionRecommendationResponse | null;
  enrichedFocusFields: Array<{
    key: string;
    title: string;
    problem: string;
    rootCause: string;
    shortTermImpact: string;
    whyNow?: string;
    businessImpact?: string;
    kpiContext?: string;
    revenueLeakMonthly?: number;
    enrichedActions?: string[];
    taskDetails?: Array<{
      title: string;
      description: string;
      owner?: string;
      dueInDays?: number;
      successMetric?: string;
      steps?: string[];
      effort?: string;
      impact?: string;
    }>;
  }>;
  quickWins: Array<{
    title: string;
    whyItWorks: string;
    implementation?: string;
    owner?: string;
    deadline?: string;
    successMetric?: string;
    expectedImpact?: string;
  }>;
}

interface RecData {
  rank: number;
  category: string;
  title: string;
  urgencyLabel: string;
  urgencyValue: string;
  timeframe: string;
  timeframePhase: string;
  headline: string;
  situationText: string;
  rootCauseText: string;
  consequenceText: string;
  kpiContext: string;
  measures: Array<{ title: string; body: string; lever: string }>;
  successCriteria: Array<{ metric: string; target: string }>;
  whyNow: string;
  impactStatement: string;
  firstStep: string;
}

const RC = [
  {
    grad: 'from-[#1e3a8a] to-[#2563eb]',
    badge: 'bg-blue-600',
    badgeLight: 'bg-blue-50 text-blue-700 border-blue-200',
    ring: 'ring-blue-300',
    border: 'border-blue-200',
    accent: 'text-blue-600',
    accentBg: 'bg-blue-50',
    accentBorder: 'border-blue-100',
    pill: 'bg-blue-100 text-blue-800',
    label: 'text-blue-700',
    dot: 'bg-blue-500',
  },
  {
    grad: 'from-[#0f172a] to-[#334155]',
    badge: 'bg-slate-700',
    badgeLight: 'bg-slate-50 text-slate-700 border-slate-200',
    ring: 'ring-slate-300',
    border: 'border-slate-200',
    accent: 'text-slate-700',
    accentBg: 'bg-slate-50',
    accentBorder: 'border-slate-100',
    pill: 'bg-slate-100 text-slate-800',
    label: 'text-slate-700',
    dot: 'bg-slate-500',
  },
  {
    grad: 'from-[#4c1d95] to-[#7c3aed]',
    badge: 'bg-violet-700',
    badgeLight: 'bg-violet-50 text-violet-700 border-violet-200',
    ring: 'ring-violet-300',
    border: 'border-violet-200',
    accent: 'text-violet-700',
    accentBg: 'bg-violet-50',
    accentBorder: 'border-violet-100',
    pill: 'bg-violet-100 text-violet-800',
    label: 'text-violet-700',
    dot: 'bg-violet-500',
  },
  {
    grad: 'from-[#064e3b] to-[#059669]',
    badge: 'bg-emerald-700',
    badgeLight: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ring: 'ring-emerald-300',
    border: 'border-emerald-200',
    accent: 'text-emerald-700',
    accentBg: 'bg-emerald-50',
    accentBorder: 'border-emerald-100',
    pill: 'bg-emerald-100 text-emerald-800',
    label: 'text-emerald-700',
    dot: 'bg-emerald-500',
  },
  {
    grad: 'from-[#78350f] to-[#d97706]',
    badge: 'bg-amber-600',
    badgeLight: 'bg-amber-50 text-amber-700 border-amber-200',
    ring: 'ring-amber-300',
    border: 'border-amber-200',
    accent: 'text-amber-700',
    accentBg: 'bg-amber-50',
    accentBorder: 'border-amber-100',
    pill: 'bg-amber-100 text-amber-800',
    label: 'text-amber-700',
    dot: 'bg-amber-500',
  },
];

function buildFullRecs(props: Top5Props): RecData[] {
  const { typedResult, aiStructuredOutput, enrichedFocusFields } = props;
  const metrics = typedResult?.computedMetrics;
  const team = typedResult?.teamPerformance;
  const signals = team?.summarySignals;
  const salesCycleMonths = typedResult?.normalizedData?.salesCycleMonths ?? 3;
  const avgDealSize = typedResult?.normalizedData?.averageDealSize ?? 0;
  const fmtE = (v: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
  const fmtP = (v: number) => `${Math.round(v)} %`;

  // ── Kontext-Daten ───────────────────────────────────────────────────────
  const c2d = Math.round(metrics?.convContactToDiscovery ?? 0);
  const benchC2D = Math.round((metrics as unknown as Record<string, number>)?.benchmarkContactToDiscovery ?? 6);
  const d2p = Math.round(metrics?.convDiscoveryToPresentation ?? 0);
  const benchD2P = Math.round((metrics as unknown as Record<string, number>)?.benchmarkDiscoveryToPresentation ?? 50);
  const p2d = Math.round(metrics?.convPresentationToDeal ?? 0);
  const benchP2D = Math.round((metrics as unknown as Record<string, number>)?.benchmarkPresentationToDeal ?? 28);
  const productiveReps = team?.productiveReps ?? 0;
  const targetPerRepMonth = team?.targetPerRepMonth ?? 0;
  const projectedGap = team?.projectedYearEndGap ?? 0;
  const annualTarget = (typedResult?.teamPerformance?.totalRevenueTargetYear ?? 0);
  const hasPacing = signals?.hasPacingProblem ?? false;
  const hasVariance = signals?.hasVarianceProblem ?? false;
  const hasReview = signals?.hasReviewProblem ?? false;
  const hasPipeline = signals?.hasPipelineProblem ?? false;

  // Fokusfelder — nur für Kontext-Texte
  const f1 = enrichedFocusFields[0];
  const f2 = enrichedFocusFields[1];
  const f3 = enrichedFocusFields[2];

  // Größte Revenue-Lücke aus allen Fokusfeldern
  const topLeak = Math.max(
    f1?.revenueLeakMonthly ?? 0,
    f2?.revenueLeakMonthly ?? 0,
    f3?.revenueLeakMonthly ?? 0
  );
  const topLeakFmt = topLeak > 0 ? fmtE(topLeak) : null;

  // KI-Prioritäten
  const aiP1ext = (aiStructuredOutput?.top_3_priorities?.[0] as unknown as Record<string, unknown>);
  const aiP2ext = (aiStructuredOutput?.top_3_priorities?.[1] as unknown as Record<string, unknown>);
  const aiP3ext = (aiStructuredOutput?.top_3_priorities?.[2] as unknown as Record<string, unknown>);

  const recs: RecData[] = [];

  // ═══════════════════════════════════════════════════════════════════════
  // EMPFEHLUNG 1 — Pipeline & Lead-Generierung
  // Thema: Kontaktvolumen, Lead-Ownership, Erstgespräch-Rate, Marktzugang
  // ═══════════════════════════════════════════════════════════════════════
  {
    const f = enrichedFocusFields[0];
    const revLeak = f?.revenueLeakMonthly && f.revenueLeakMonthly > 0 ? fmtE(f.revenueLeakMonthly) : topLeakFmt;
    const gapC2D = Math.max(0, benchC2D - c2d);
    const hasC2DGap = gapC2D > 0;
    const aiTasks = (aiP1ext?.task_details as Array<{ title?: string; description?: string }> | undefined);
    const measures1: RecData['measures'] = [];
    if (aiTasks && aiTasks.length >= 2) {
      aiTasks.slice(0, 5).forEach(t => { if (t.title) measures1.push({ title: t.title, body: t.description ?? '', lever: 'KI-Empfehlung' }); });
    }
    if (measures1.length < 5) measures1.push({
      title: hasC2DGap ? 'Lead-Ownership und 24h-Reaktionszeit verbindlich einführen' : 'Kontaktvolumen und Marktpräsenz gezielt ausbauen',
      body: hasC2DGap
        ? `Jeder eingehende Kontakt erhält innerhalb von 24 Stunden einen namentlich definierten Owner. Kein Lead ohne dokumentierten nächsten Schritt im CRM. Aktuell werden ${fmtP(c2d)} der Kontakte zu Erstgesprächen — der B2B-Referenzwert liegt bei ${fmtP(benchC2D)}. Die Lücke ist kein Ressourcenproblem, sondern ein Prozess- und Ownership-Problem.`
        : `Mit ${fmtP(c2d)} Erstgespräch-Rate übertrifft Ihr Team den B2B-Referenzwert (${fmtP(benchC2D)}) deutlich. Die Erstgespräch-Conversion ist eine Stärke — das Wachstumspotenzial liegt im Volumen: Mehr qualifizierte Kontakte, mehr Marktpräsenz, breiterer Outreach.`,
      lever: hasC2DGap ? 'Lead-Ownership' : 'Pipeline-Volumen',
    });
    if (measures1.length < 5) measures1.push({
      title: 'Inbound- und Outbound-Routing klar trennen',
      body: 'Inbound-Leads (hohe Kaufabsicht, sofortiger Owner, Buchungslink in Erstreaktion) und Outbound-Kontakte (Awareness-Phase, Nurturing-Sequenz, längere Qualifizierungszeit) erfordern grundlegend andere Routing-Logik. Was nicht differenziert wird, wird falsch priorisiert — und falsch priorisierte Leads kosten Conversion.',
      lever: 'Segmentierung',
    });
    if (measures1.length < 5) measures1.push({
      title: 'Discovery-Buchungsflow auf maximale Reibungsfreiheit optimieren',
      body: 'Jede Erstreaktion enthält einen direkten Buchungslink — kein Ping-Pong über Verfügbarkeiten. Ziel: Erstgespräch innerhalb von 48h nach Erstkontakt. Jeder zusätzliche Tag Verzögerung reduziert die Buchungswahrscheinlichkeit um messbare Prozentpunkte. Buchungsflow als CRM-Automation abbilden und monatlich auf Abbruchpunkte prüfen.',
      lever: 'Reibungsreduktion',
    });
    if (measures1.length < 5) measures1.push({
      title: 'Akquise-Mix auf skalierbare Kanäle ausrichten',
      body: 'Welche Kanäle liefern die höchste Kontakt-zu-Erstgespräch-Rate? Diese Kanäle bekommen mehr Ressourcen — nicht gleichmäßige Verteilung über alle Kanäle. Outbound (Cold Call, LinkedIn), Inbound (Content, SEO), Referral und Partner haben fundamental unterschiedliche Conversion-Profile. Kanal-Analyse quartalsweise, Budget-Shift konsequent.',
      lever: 'Kanal-Effizienz',
    });
    if (measures1.length < 5) measures1.push({
      title: 'Wöchentlichen Lead-SLA-Review etablieren',
      body: `Jeden Montag: Alle Leads der letzten 7 Tage auf Reaktionszeit, Owner und nächsten Schritt prüfen. Leads ohne Owner nach 24h eskalieren automatisch. ${hasC2DGap ? `Ziel in 30 Tagen: Erstgespräch-Rate von ${fmtP(c2d)} auf ${fmtP(Math.min(benchC2D, c2d + Math.min(gapC2D, 10)))} steigern.` : `Das bestehende Niveau von ${fmtP(c2d)} sichern und Kontaktvolumen pro Rep um 20 % steigern.`}`,
      lever: 'Steuerungsroutine',
    });

    recs.push({
      rank: 1,
      category: 'Pipeline & Lead-Generierung',
      title: 'Pipeline-Aufbau und Lead-to-Opportunity',
      urgencyLabel: revLeak ? 'Monatliches Potenzial' : 'Priorität',
      urgencyValue: revLeak ?? 'Hoch',
      timeframe: '0 – 30 Tage',
      timeframePhase: 'Phase 1 · Sofortmaßnahmen',
      headline: (aiP1ext?.title as string) || (f?.title ?? 'Pipeline-Aufbau und Lead-Ownership'),
      situationText: f?.problem ?? `Das Team erzielt aktuell ${fmtP(c2d)} Kontakt-zu-Erstgespräch-Rate${annualTarget > 0 ? ` — bei einem Jahresziel von ${fmtE(annualTarget)}` : ''}. ${hasC2DGap ? `Der B2B-Referenzwert von ${fmtP(benchC2D)} wird nicht erreicht.` : `Das liegt über dem B2B-Referenzwert von ${fmtP(benchC2D)} — der Hebel ist Volumenwachstum.`}`,
      rootCauseText: f?.rootCause ?? (hasC2DGap
        ? 'Fehlende Lead-Ownership, unklare Reaktionszeit-Standards und mangelnde Routing-Logik führen dazu, dass qualifizierte Kontakte den Funnel verlassen, bevor ein Gespräch stattfindet.'
        : 'Die Erstgespräch-Rate ist stark. Die Lücke zum Ziel entsteht nicht durch schlechte Conversion, sondern durch zu geringes Kontaktvolumen und fehlende Kanal-Skalierung.'),
      consequenceText: revLeak
        ? `Ohne Optimierung des oberen Funnels verlässt monatlich strukturell gebundenes Umsatzpotenzial von ${revLeak} den Vertrieb — ohne dass eine neue Ursache entstehen muss.`
        : `Selbst bei guter Erstgespräch-Rate bleibt das Jahresziel unerreichbar, wenn das Kontaktvolumen nicht mit dem Zielpfad mithalten kann.`,
      kpiContext: f?.kpiContext ?? `C2D: ${fmtP(c2d)} | Benchmark: ${fmtP(benchC2D)} | ${hasC2DGap ? `Lücke: ${gapC2D}pp` : `Vorsprung: ${Math.abs(benchC2D - c2d)}pp`}`,
      measures: measures1.slice(0, 5),
      successCriteria: [
        { metric: 'Kurzfristiges Ziel', target: f?.shortTermImpact ?? (hasC2DGap ? `Erstgespräch-Rate von ${fmtP(c2d)} auf ${fmtP(Math.min(benchC2D, c2d + 5))} steigern` : `Kontaktvolumen je Rep um 20 % steigern ohne Qualitätsverlust`) },
        { metric: 'Messbares Signal', target: hasC2DGap ? 'Wöchentlicher Anstieg der Kontakt-zu-Discovery-Rate im CRM' : 'Wöchentlicher Anstieg der Kontakte und Erstgespräche je Rep' },
        { metric: 'Zeitfenster', target: '4 Wochen Implementierung, danach monatliches Tracking' },
      ],
      whyNow: (aiP1ext?.why_now as string) || f?.whyNow || 'Der obere Funnel bestimmt alles Downstream. Ohne ausreichendes Kontaktvolumen und saubere Lead-Bearbeitung kann keine Conversion-Optimierung weiter unten im Funnel kompensieren.',
      impactStatement: revLeak ? `${revLeak}/Monat Potenzial aus bestehendem Marktvolumen` : 'Messbar mehr Pipeline aus vorhandenen Kanälen — ohne Mehrinvestition',
      firstStep: 'Heute: CRM-Pflichtfeld „Lead-Owner" und „Reaktionszeit" aktivieren. Im nächsten Team-Meeting 24h-SLA als verbindlichen Standard kommunizieren.',
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // EMPFEHLUNG 2 — Conversion & Qualifizierung
  // Thema: Discovery → Präsentation → Abschluss, Qualifizierungstiefe
  // ═══════════════════════════════════════════════════════════════════════
  {
    const f = enrichedFocusFields[1] ?? enrichedFocusFields[0];
    const revLeak2 = f?.revenueLeakMonthly && f.revenueLeakMonthly > 0 ? fmtE(f.revenueLeakMonthly) : null;
    const gapD2P = Math.max(0, benchD2P - d2p);
    const gapP2D = Math.max(0, benchP2D - p2d);
    const hasD2PGap = gapD2P > 0;
    const hasP2DGap = gapP2D > 0;
    // Drei klar unterschiedliche Szenarien:
    // A) Discovery-Lücke: d2p < benchD2P → Qualifizierungstiefe verbessern
    // B) Closing-Lücke: d2p ok, aber p2d < benchP2D → Abschlussdisziplin
    // C) Beide stark: d2p >= benchD2P && p2d >= benchP2D → Volumen & Cycle
    const bothStrong = !hasD2PGap && !hasP2DGap;
    const aiTasks2 = (aiP2ext?.task_details as Array<{ title?: string; description?: string }> | undefined);
    const measures2: RecData['measures'] = [];
    if (aiTasks2 && aiTasks2.length >= 2) {
      aiTasks2.slice(0, 5).forEach(t => { if (t.title) measures2.push({ title: t.title, body: t.description ?? '', lever: 'KI-Empfehlung' }); });
    }

    if (bothStrong) {
      // SZENARIO C: Beide Raten über Benchmark → Volumen, Cycle-Speed, Skalierung
      if (measures2.length < 5) measures2.push({
        title: 'Deal-Volumen durch gezieltes Kontaktwachstum ausbauen',
        body: `Ihre Conversion ist stark: ${fmtP(d2p)} Discovery-zu-Angebot und ${fmtP(p2d)} Closing-Rate liegen deutlich über dem B2B-Referenzwert. Das Wachstumspotenzial liegt nicht in der Verbesserung der Raten — sondern im Volumen. Mehr qualifizierte Kontakte bei dieser Closing-Stärke bedeuten direkt mehr Abschlüsse. Kontakt-Ziel pro Rep erhöhen und Outreach-Kapazität systematisch ausbauen.`,
        lever: 'Pipeline-Volumen',
      });
      if (measures2.length < 5) measures2.push({
        title: 'Sales Cycle aktiv verkürzen — jede Woche zählt',
        body: `Bei einer Closing-Rate von ${fmtP(p2d)} ist die Frage nicht ob ein Deal schließt, sondern wann. Jede Woche Cycle-Verkürzung bedeutet direkt frühere Liquidität${avgDealSize > 0 ? ` — bei ${fmtE(avgDealSize)} Ø Deal-Größe` : ''}. Engpass-Analyse: Wo dauert es am längsten — Discovery-to-Proposal oder Proposal-to-Decision? Diesen einen Engpass um 30 % reduzieren.`,
        lever: 'Cycle-Beschleunigung',
      });
      if (measures2.length < 5) measures2.push({
        title: 'Conversion-Qualität dokumentieren und gegen Regression absichern',
        body: `Eine ${fmtP(p2d)} Closing-Rate ist eine außergewöhnliche Stärke — und ein Risiko, wenn sie undokumentiert bleibt. Was führt zu dieser Rate? Welche Gesprächsführung, welche Qualifizierungstiefe, welche Entscheider-Arbeit? Dieses Wissen muss ins Playbook — replizierbar für jeden neuen Rep.`,
        lever: 'Qualitätssicherung',
      });
      if (measures2.length < 5) measures2.push({
        title: 'Upsell- und Expansion-Potenzial bei Bestandskunden aktivieren',
        body: 'Wer gut abschließt, kann auch gut erweitern. Bestehende Kunden haben 5–7× höhere Kaufwahrscheinlichkeit als Neukunden. Strukturierter Prozess: Quartalsweise Review, Upsell-Trigger im CRM, Rep-Ziele auch auf Expansion-Revenue. Der schnellste Weg zu mehr Umsatz ist oft der bestehende Kunde.',
        lever: 'Expansion Revenue',
      });
      if (measures2.length < 5) measures2.push({
        title: 'Referral-Systematik aus starker Abschluss-Performance ableiten',
        body: `Kunden, die mit ${fmtP(p2d)} Closing-Rate gewonnen wurden, sind in der Regel hochzufrieden. Diese Kunden sind potenzielle Referral-Geber. Strukturierter Prozess: Nach jedem Abschluss innerhalb von 60 Tagen aktive Referral-Anfrage. Referrals schließen im Schnitt 30 % schneller und mit höherem Ø Deal-Wert.`,
        lever: 'Referral-Engine',
      });
    } else if (hasD2PGap) {
      // SZENARIO A: Discovery-Lücke → Qualifizierungstiefe verbessern
      if (measures2.length < 5) measures2.push({
        title: 'Strukturiertes Discovery-Framework einführen',
        body: `Jedes Erstgespräch folgt einem 5-Punkte-Rahmen: (1) Pain identifizieren, (2) Priorität und Dringlichkeit klären, (3) Budget verstehen, (4) Buying-Center kartieren, (5) nächsten Schritt festlegen. Aktuell: ${fmtP(d2p)} Discovery-zu-Angebot (Referenz: ${fmtP(benchD2P)}). Die ${gapD2P}pp Lücke entsteht fast ausnahmslos durch fehlende Qualifizierungstiefe.`,
        lever: 'Discovery-Qualität',
      });
      if (measures2.length < 5) measures2.push({
        title: '„No-Proposal-without-Qualification"-Regel einführen',
        body: 'Kein Angebot ohne dokumentierten Pain, Budget-Rahmen, Entscheider-Kontakt und verbindliche Timeline im CRM. Angebote an nicht qualifizierte Opportunities kosten Vorbereitung und Follow-up-Energie bei nahezu null Abschlusswahrscheinlichkeit.',
        lever: 'Qualifizierungsdisziplin',
      });
      if (measures2.length < 5) measures2.push({
        title: 'Pain-Vertiefung als Gesprächstechnik trainieren',
        body: `Die Rate von ${fmtP(d2p)} zeigt: Gespräche wechseln zu früh in die Lösungsphase. Pain ohne Dringlichkeit erzeugt keine Kaufentscheidung. Monatliches Roleplay mit echten Discovery-Szenarien — Gesprächstraining, nicht Theorie.`,
        lever: 'Gesprächskompetenz',
      });
      if (measures2.length < 5) measures2.push({
        title: 'Buying-Center kartieren und Entscheider sicherstellen',
        body: 'In jedem Discovery Call: Wer ist der wirtschaftliche Entscheider? Wer hat Veto? Wer empfiehlt? Kein Deal ohne Entscheidungslandkarte — sonst landet das Angebot beim falschen Ansprechpartner.',
        lever: 'Stakeholder-Intelligenz',
      });
      if (measures2.length < 5) measures2.push({
        title: 'Gesprächs-Scorecard für Discovery einführen',
        body: 'Jedes Erstgespräch anhand von 6 Kriterien bewerten: Pain-Tiefe, Budget, Timeline, Entscheider, nächster Schritt, Commitment-Level. Score unter 4 von 6 = kein Angebot. Wöchentliche Auswertung je Rep — Basis für gezieltes Coaching.',
        lever: 'Qualitätsmessung',
      });
    } else {
      // SZENARIO B: Closing-Lücke → Abschlussdisziplin & Follow-up
      if (measures2.length < 5) measures2.push({
        title: 'Closing-Sequenz für jedes Angebot verbindlich machen',
        body: `Jedes Angebot endet mit dokumentiertem nächsten Schritt, fixiertem Entscheidungstermin und schriftlichem Commitment. Aktuell: ${fmtP(p2d)} Closing-Rate (Referenz: ${fmtP(benchP2D)}). Die ${gapP2D}pp Lücke entsteht durch fehlende Abschluss-Disziplin, nicht durch fehlende Angebots-Qualität.`,
        lever: 'Abschlussdisziplin',
      });
      if (measures2.length < 5) measures2.push({
        title: 'Strukturierte Follow-up-Sequenz automatisieren',
        body: 'Tag 3: Empfangsbestätigung + inhaltliche Rückfrage. Tag 7: Relevanter Mehrwert (Case Study, ROI-Rechnung). Tag 14: Direkte Entscheidungsanfrage mit Alternativdatum. Im CRM automatisiert für alle offenen Angebote.',
        lever: 'Follow-up-Prozess',
      });
      if (measures2.length < 5) measures2.push({
        title: 'Entscheider-Präsenz beim finalen Angebot sicherstellen',
        body: 'Kein finales Angebot ohne Präsentation vor dem wirtschaftlichen Entscheider. Angebote per E-Mail an Sachbearbeiter haben 40–60 % niedrigere Abschlussquote. „Ich schicke das weiter" muss aktiv gesteuert werden.',
        lever: 'Entscheider-Zugang',
      });
      if (measures2.length < 5) measures2.push({
        title: 'Einwand-Bibliothek aufbauen und trainieren',
        body: 'Top 10 Einwände der letzten 12 Monate dokumentieren mit bewährten Antwortstrategien. Monatliches Einwand-Training auf Basis echter Closing-Situationen — nicht generischer Verkaufsseminare.',
        lever: 'Einwandbehandlung',
      });
      if (measures2.length < 5) measures2.push({
        title: 'Win/Loss-Analyse nach jedem verlorenen Deal',
        body: 'Jeder verlorene Deal innerhalb von 7 Tagen analysieren: Warum verloren? Wer hat entschieden? Was hätte den Ausgang verändert? Nach 8 Wochen entstehen Muster — Basis für gezieltes Closing-Coaching.',
        lever: 'Lernschleife',
      });
    }

    recs.push({
      rank: 2,
      category: 'Conversion & Qualifizierung',
      title: bothStrong ? 'Conversion-Stärke skalieren und Volumen ausbauen' : 'Funnel-Conversion von Discovery bis Abschluss',
      urgencyLabel: revLeak2 ? 'Monatliches Potenzial' : 'Priorität',
      urgencyValue: revLeak2 ?? (bothStrong ? 'Mittel' : 'Hoch'),
      timeframe: bothStrong ? '0 – 60 Tage' : '0 – 45 Tage',
      timeframePhase: 'Phase 1–2 · Parallel zu Empfehlung 1',
      headline: (aiP2ext?.title as string) || (
        bothStrong ? 'Starke Conversion-Basis für Wachstum nutzen'
        : hasD2PGap ? 'Discovery-Qualität und Qualifizierungstiefe verbessern'
        : 'Closing-Disziplin und Abschlussquote steigern'
      ),
      situationText: bothStrong
        ? `Ihre Conversion-Raten sind eine echte Stärke: ${fmtP(d2p)} Discovery-zu-Angebot (Referenz: ${fmtP(benchD2P)}) und ${fmtP(p2d)} Closing-Rate (Referenz: ${fmtP(benchP2D)}). Überdurchschnittliche Performance. Der Hebel liegt nicht in der Rate-Verbesserung — sondern im Ausbauen des Volumens und der Skalierung dieser Stärke.`
        : `Aktuell: ${fmtP(d2p)} Discovery-zu-Angebot (Referenz: ${fmtP(benchD2P)}) und ${fmtP(p2d)} Closing-Rate (Referenz: ${fmtP(benchP2D)}). ${hasD2PGap ? 'Die Qualifizierungstiefe im Discovery Call ist der größte Hebel.' : 'Die Qualifizierung ist gut — der Engpass liegt im Closing und Follow-up.'}`,
      rootCauseText: bothStrong
        ? 'Starke Conversion-Raten entstehen durch gute Qualifizierung und konsequente Abschluss-Disziplin. Diese Stärke ist oft undokumentiert — und damit nicht skalierbar. Wenn Top-Performer das Team verlassen, bricht die Rate ein.'
        : hasD2PGap
        ? 'Gespräche wechseln zu früh in die Lösungsphase, bevor Pain, Budget und Entscheidungsrahmen vollständig verstanden sind. Qualifizierung ohne Tiefe erzeugt Angebote, die niemand entscheiden kann.'
        : 'Die Qualifizierung ist stark. Abschlüsse scheitern am fehlenden Entscheider-Zugang, mangelnder Follow-up-Disziplin oder unzureichender Einwandbehandlung nach Angebotsversand.',
      consequenceText: bothStrong
        ? 'Stärken, die nicht dokumentiert und skaliert werden, sind anfällig. Mehr Kontakte + gleiche Conversion = direkt mehr Umsatz — dieser Hebel liegt brach.'
        : revLeak2
        ? `Jeder Monat ohne Conversion-Optimierung bedeutet ${revLeak2} verlorenes Umsatzpotenzial aus dem bestehenden Funnel.`
        : 'Conversion-Lücken akkumulieren — jede nicht geschlossene Opportunity ist dauerhaft verloren, nicht verschoben.',
      kpiContext: `D2P: ${fmtP(d2p)} (Ref: ${fmtP(benchD2P)}) | P2D: ${fmtP(p2d)} (Ref: ${fmtP(benchP2D)})${bothStrong ? ' | ✓ Beide Stufen überdurchschnittlich' : ''}${hasD2PGap ? ` | Discovery-Lücke: ${gapD2P}pp` : ''}${hasP2DGap ? ` | Closing-Lücke: ${gapP2D}pp` : ''}`,
      measures: measures2.slice(0, 5),
      successCriteria: [
        {
          metric: 'Kurzfristiges Ziel',
          target: bothStrong
            ? 'Kontaktvolumen je Rep um 20 % steigern bei gleichbleibend hoher Closing-Rate'
            : hasD2PGap
            ? `Discovery-zu-Angebot-Rate von ${fmtP(d2p)} auf ${fmtP(Math.min(benchD2P, d2p + 10))} in 8 Wochen`
            : `Closing-Rate von ${fmtP(p2d)} auf ${fmtP(Math.min(p2d + 8, 65))} in 8 Wochen`,
        },
        {
          metric: 'Messbares Signal',
          target: bothStrong
            ? 'Anstieg der monatlichen Abschlüsse bei stabiler Closing-Rate'
            : hasD2PGap
            ? 'Mehr Discovery Calls mit vollständig ausgefüllten CRM-Qualifizierungsfeldern'
            : 'Kürzere Zeit zwischen Angebotsversand und Entscheidung',
        },
        {
          metric: 'Zeitfenster',
          target: bothStrong ? '8 Wochen Volumen-Aufbau, danach monatliches Tracking' : '6 Wochen Implementierung, 8 Wochen bis erste Messbarkeit',
        },
      ],
      whyNow: (aiP2ext?.why_now as string) || f?.whyNow || (bothStrong
        ? 'Starke Conversion-Raten sind das Fundament für skalierbares Wachstum. Jetzt ist der richtige Zeitpunkt, dieses Fundament systematisch zu nutzen — bevor Fluktuation oder Wachstum die undokumentierte Stärke verwässern.'
        : 'Jede Conversion-Stufe unter Benchmark multipliziert den Verlust über den gesamten Funnel.'),
      impactStatement: bothStrong
        ? `Mehr Abschlüsse aus vorhandener Stärke — durch reines Volumen${avgDealSize > 0 ? ` (${fmtE(avgDealSize)} Ø Deal)` : ''}`
        : revLeak2 ? `${revLeak2}/Monat Effizienzgewinn aus bestehendem Funnel` : 'Messbar höhere Abschlussquote aus gleichem Leadvolumen',
      firstStep: bothStrong
        ? 'Diese Woche: Closing-Wissen der besten Reps dokumentieren. Nächste Woche: Kontakt-Ziel je Rep um 20 % anheben.'
        : hasD2PGap
        ? 'Nächste Woche: CRM-Pflichtfelder für Pain, Budget, Entscheider und Timeline aktivieren.'
        : 'Nächste Woche: Follow-up-Sequenz für alle offenen Angebote im CRM als Automation hinterlegen.',
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // EMPFEHLUNG 3 — Rep-Performance & Zielerreichung
  // Thema: Pacing, individuelle Ziele, Performance-Varianz, Teamentwicklung
  // ═══════════════════════════════════════════════════════════════════════
  {
    const f = enrichedFocusFields[2] ?? enrichedFocusFields[1];
    const revLeak3 = f?.revenueLeakMonthly && f.revenueLeakMonthly > 0 ? fmtE(f.revenueLeakMonthly) : null;
    const currentRevPerRep = typedResult?.repExecutionPath?.currentRevenuePerRep ?? 0;
    const pacingPct = (targetPerRepMonth > 0 && currentRevPerRep > 0) ? Math.round((currentRevPerRep / targetPerRepMonth) * 100) : 0;
    const aiTasks3 = (aiP3ext?.task_details as Array<{ title?: string; description?: string }> | undefined);
    const measures3: RecData['measures'] = [];
    if (aiTasks3 && aiTasks3.length >= 2) {
      aiTasks3.slice(0, 5).forEach(t => { if (t.title) measures3.push({ title: t.title, body: t.description ?? '', lever: 'KI-Empfehlung' }); });
    }
    if (measures3.length < 5) measures3.push({
      title: 'Individuelle Rep-Ziele operativ herunterbrechen',
      body: `Das Jahresziel${annualTarget > 0 ? ` von ${fmtE(annualTarget)}` : ''} muss auf Quartale, Monate, Wochen und tägliche Aktivitäts-KPIs heruntergebrochen werden. Jeder Rep kennt täglich seinen Ist-Stand, seinen Gap und seinen priorisierten nächsten Schritt. ${hasPacing ? `Aktuell liegt das Team bei ca. ${pacingPct} % des Sollverlaufs — ohne wöchentliche Sichtbarkeit ist rechtzeitiges Gegensteuern unmöglich.` : 'Selbst bei gutem Pacing schafft operative Transparenz Eigenverantwortung und frühe Eskalation.'}`,
      lever: 'Ziel-Kaskade',
    });
    if (measures3.length < 5) measures3.push({
      title: 'Top-Performer-Muster extrahieren und als Standard definieren',
      body: 'Was machen die besten 20 % des Teams in Discovery, Einwandbehandlung und Closing konkret anders? Daten aus CRM-Aktivitäten, Call-Aufzeichnungen und Win/Loss-Analysen aggregieren. Dieses Verhaltensmuster wird als Mindeststandard für alle Reps definiert und in einem internen Playbook dokumentiert. Performance-Varianz ist fast immer ein Standards-Problem, kein Talent-Problem.',
      lever: 'Best Practice Transfer',
    });
    if (measures3.length < 5) measures3.push({
      title: 'Monatliche Gesprächsanalyse als Coaching-Instrument verankern',
      body: '3–5 aufgezeichnete Discovery Calls und Closing-Gespräche je Rep monatlich gemeinsam analysieren: Was wird gefragt, was nicht? Wann verliert das Gespräch Momentum? Wann wird zu früh auf Lösung gegangen? Faktenbasiertes Coaching auf Basis echter Gespräche ist 3× wirksamer als generische Verkaufstrainings nach Bauchgefühl.',
      lever: 'Faktenbasiertes Coaching',
    });
    if (measures3.length < 5) measures3.push({
      title: 'Peer-Learning-Sessions monatlich verankern',
      body: 'Jeden Monat teilt ein Top-Performer in 20 Minuten einen konkreten Erfahrungsbericht: Welcher Ansatz hat bei welchem Kundentyp funktioniert, und warum? Format: Echte Fallstudie + 10 Minuten offene Fragen. Kein aufgesetztes Training — echter Praxistransfer aus dem laufenden Vertrieb. Wissen, das nicht dokumentiert und weitergegeben wird, verlässt das Unternehmen mit der nächsten Kündigung.',
      lever: 'Wissenstransfer',
    });
    if (measures3.length < 5) measures3.push({
      title: productiveReps > 0 && targetPerRepMonth > 0 ? `Recovery-Plan für Reps unter 70 % des Monatsziels` : 'Individuellen Entwicklungsplan je Rep einführen',
      body: productiveReps > 0 && targetPerRepMonth > 0
        ? `Reps unter 70 % des Monatsziels (${fmtE(Math.round(targetPerRepMonth * 0.7))} statt ${fmtE(targetPerRepMonth)}) nach 4 Wochen erhalten einen strukturierten Aktionsplan mit wöchentlichen Meilensteinen und dediziertem Coaching. Frühzeitige Intervention verhindert, dass temporäre Tiefs zu strukturellen Problemen werden.`
        : 'Jeder Rep kennt seine 2–3 wichtigsten Entwicklungsfelder für das nächste Quartal. Quartalsgespräch: Was hat sich verbessert? Was ist der nächste Schritt? Gezielte individuelle Entwicklung schlägt generische Trainings in Effektivität und Motivation.',
      lever: hasVariance ? 'Frühintervention' : 'Individuelle Entwicklung',
    });

    recs.push({
      rank: 3,
      category: 'Rep-Performance & Zielerreichung',
      title: 'Individuelle Leistung und Team-Pacing',
      urgencyLabel: revLeak3 ? 'Langfristiges Potenzial' : 'Priorität',
      urgencyValue: revLeak3 ?? 'Mittel',
      timeframe: '0 – 60 Tage',
      timeframePhase: 'Phase 1–2 · Parallel starten',
      headline: (aiP3ext?.title as string) || 'Rep-Performance systematisch auf Zielniveau bringen',
      situationText: hasPacing && projectedGap > 0
        ? `Das Team liegt bei ca. ${pacingPct} % des linearen Sollverlaufs — projiziert auf das Jahresende ergibt sich eine Ziellücke von ${fmtE(projectedGap)}. Monatsziel je Rep: ${fmtE(targetPerRepMonth)}.`
        : hasVariance
        ? `Die Teamleistung ist ungleich verteilt: Top-Performer liefern deutlich mehr als der Median. Die Lücke liegt nicht in der Kapazität, sondern in fehlenden Standards und ungleichem Coaching.`
        : `${productiveReps > 0 ? `${productiveReps} aktive Reps` : 'Das Team'} bewegt sich nahe am Sollverlauf. Der Fokus liegt jetzt auf Kontinuität, Teamentwicklung und Performance-Sicherung.`,
      rootCauseText: hasPacing
        ? 'Ohne wöchentliche Sichtbarkeit auf individuelle Pacing-Daten werden Abweichungen erst am Monatsende sichtbar — zu spät für effektives Gegensteuern.'
        : hasVariance
        ? 'Wenn die besten Reps das 2–3-fache des Median-Reps erzielen, liegt die Ursache fast immer in fehlenden Standards, nicht in fehlendem Talent. Was beim einen funktioniert, bleibt beim anderen ungenutzt.'
        : 'Auch bei guter Performance ist kontinuierliche Entwicklung der Schlüssel zu nachhaltigem Wachstum.',
      consequenceText: projectedGap > 0
        ? `Bei unverändertem Verlauf fehlen zum Jahresende ${fmtE(projectedGap)}. Diese Lücke ist noch schließbar — aber nur mit wöchentlicher Intervention, nicht mit monatlichen Reviews.`
        : 'Teamleistung ohne kontinuierliche Entwicklung stagniert. Top-Performer verlassen Teams ohne Entwicklungsperspektive.',
      kpiContext: `${productiveReps > 0 ? `${productiveReps} Reps` : ''} | Pacing: ${pacingPct > 0 ? `${pacingPct} %` : 'n/a'} | Monatsziel/Rep: ${targetPerRepMonth > 0 ? fmtE(targetPerRepMonth) : 'n/a'}${projectedGap > 0 ? ` | Proj. Lücke: ${fmtE(projectedGap)}` : ''}`.trim(),
      measures: measures3.slice(0, 5),
      successCriteria: [
        { metric: 'Kurzfristiges Ziel', target: hasPacing ? `Pacing auf ≥ 90 % des Sollverlaufs bringen in 6 Wochen` : 'Performance-Varianz im Team auf Faktor ≤ 1,5 reduzieren' },
        { metric: 'Messbares Signal', target: hasPacing ? 'Wöchentliche Pacing-Rate je Rep im CRM sichtbar' : 'Sinkende Varianz zwischen Top- und Median-Performer nach 60 Tagen' },
        { metric: 'Zeitfenster', target: '4 Wochen Setup, 8 Wochen bis erste messbare Wirkung' },
      ],
      whyNow: (aiP3ext?.why_now as string) || f?.whyNow || 'Operative Maßnahmen ohne individuelle Rep-Sichtbarkeit verpuffen in der Ausführung. Pacing und Performance-Entwicklung sind die Brücke zwischen Strategie und Ergebnis.',
      impactStatement: revLeak3 ? `${revLeak3}/Monat Multiplikator-Potenzial` : projectedGap > 0 ? `Bis zu ${fmtE(Math.round(projectedGap / 12))}/Monat schließbare Lücke` : 'Nachhaltige Performance-Steigerung im gesamten Team',
      firstStep: 'Heute: Wöchentlichen Pacing-Review-Slot je Rep in Kalender legen. Tracking-Sheet für Ist vs. Soll je Woche anlegen.',
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // EMPFEHLUNG 4 — Führungs- und Steuerungsinfrastruktur
  // Thema: Rhythmus, Forecast, Coaching-Qualität, Führungsdisziplin
  // ═══════════════════════════════════════════════════════════════════════
  {
    const measures4: RecData['measures'] = [];
    measures4.push({
      title: hasPacing && projectedGap > 0 ? 'Wöchentliches Pace-Tracking je Rep als Standard einführen' : 'Festen wöchentlichen Führungsrhythmus etablieren',
      body: hasPacing && projectedGap > 0
        ? `Das Team liegt nicht im linearen Sollverlauf — die projizierte Jahres-Lücke beträgt ${fmtE(projectedGap)}. Wöchentliches Pace-Review je Rep: Ist-Stand versus Wochen-Soll, Gap und konkreter Reaktionsplan. Monatliche Reviews erkennen Probleme zu spät. Wöchentliche Reviews ermöglichen rechtzeitiges Gegensteuern.`
        : 'Wöchentliches 1:1 (15 Min.) mit jedem Rep: Pipeline-Stand, aktuelle Blocker, nächster konkreter Schritt je priorisiertem Deal. Monatliches Team-Review: Pace versus Plan, Lessons Learned, Best-Practice-Sharing. Ohne festen Rhythmus ist Führung reaktiv — und reaktive Führung kommt immer zu spät.',
      lever: 'Führungsrhythmus',
    });
    measures4.push({
      title: 'Forecast-Prozess mit verbindlicher Commitment-Logik einführen',
      body: 'Monatlich committen alle Reps ihren Forecast schriftlich — mit Begründung je Opportunity. Abweichungen über 20 % werden in einem kurzen Debriefing analysiert: Welcher Deal wurde falsch eingeschätzt? Was war die Ursache? Forecast-Genauigkeit ist der früheste Indikator für Vertriebsgesundheit — und ein Führungs-KPI, keine statistische Übung.',
      lever: 'Forecast-Disziplin',
    });
    measures4.push({
      title: hasVariance ? 'Performance-Varianz als Führungsproblem systematisch adressieren' : 'Coaching-Qualität von Statusupdate auf echtes Deal-Coaching heben',
      body: hasVariance
        ? 'Wenn Top-Performer 2–3× mehr abschließen als der Median, ist das kein Talent-Problem — es ist ein Standards-Problem. Erfolgsmuster der Besten werden extrahiert und als Mindeststandard für alle Reps definiert. Führungszeit nicht auf Top-Performer, sondern auf Variance-Reduktion im mittleren Segment konzentrieren.'
        : '60 % der 1:1-Zeit für echtes Deal-Coaching nutzen — nicht für Statusupdates. Leitfrage: „Was ist der eine Schritt, der diesen Deal jetzt voranbringt?" Coaching auf Basis konkreter Deals ist die effektivste Form der Führungswirkung im Vertrieb.',
      lever: hasVariance ? 'Varianz-Reduktion' : 'Coaching-Qualität',
    });
    if (productiveReps > 0 && targetPerRepMonth > 0) {
      measures4.push({
        title: 'Recovery-Plan für Underperformer — frühzeitig, nicht am Quartalsende',
        body: `Reps unter 70 % des Monatsziels (${fmtE(Math.round(targetPerRepMonth * 0.7))}) erhalten nach 4 konsekutiven Wochen einen strukturierten Aktionsplan: Wöchentliche Meilensteine, dediziertes Coaching, klar definierte Eskalationspunkte. Der Zeitpunkt der Intervention bestimmt ihren Erfolg — am Quartalsende ist es zu spät für nachhaltige Korrekturen.`,
        lever: 'Frühintervention',
      });
    } else {
      measures4.push({
        title: 'Vertriebssteuerung von Personen auf Prozesse übertragen',
        body: 'Entscheidungsregeln, Coaching-Protokolle und Eskalationswege müssen dokumentiert und unabhängig von einzelnen Führungspersonen funktionieren. Was in einem Führungsgespräch besprochen wird, muss im System hinterlegbar und für alle sichtbar sein — nicht nur im Kopf der Führungskraft.',
        lever: 'Systemrobustheit',
      });
    }
    measures4.push({
      title: 'Vertriebssteuerung personenunabhängig und skalierbar dokumentieren',
      body: 'Prozesse, Standards, Coaching-Protokolle und Entscheidungsregeln müssen dokumentiert sein und ohne die Anwesenheit einzelner Personen funktionieren. Das Wissen eines erfahrenen Führers darf nicht in dessen Kopf enden — es gehört ins System. Skalierbarkeit beginnt mit vollständiger Dokumentation.',
      lever: 'Systemrobustheit',
    });

    recs.push({
      rank: 4,
      category: 'Führungs-Infrastruktur',
      title: 'Führungs- und Steuerungsinfrastruktur',
      urgencyLabel: 'Wirkungsebene',
      urgencyValue: 'Systemisch · Voraussetzung für alle anderen Hebel',
      timeframe: '0 – 30 Tage parallel',
      timeframePhase: 'Parallel zu Phase 1 · Sofort starten',
      headline: 'Operative Maßnahmen wirken nur mit konsequenter Führungsstruktur im Rücken.',
      situationText: hasReview
        ? 'Die Vertriebssteuerung ist reaktiv statt proaktiv: Probleme werden erkannt, wenn sie bereits eskaliert sind — nicht wenn sie entstehen. Das führt zu Quartals-Überraschungen statt steuerbarem, voraussagbarem Wachstum.'
        : 'Ohne aktiven Führungsrhythmus verpuffen operative Maßnahmen in der Umsetzung. Die besten Prozesse funktionieren nur mit einer Führungsstruktur, die Verbindlichkeit schafft und Abweichungen frühzeitig adressiert.',
      rootCauseText: 'Führung im Vertrieb ist oft zu reaktiv und zu wenig systematisch. Pipeline-Reviews finden zu selten statt, Coaching basiert auf Annahmen statt Fakten, Forecast-Abweichungen werden erst am Monatsende sichtbar — wenn Gegensteuern zu spät ist.',
      consequenceText: 'Alle anderen Maßnahmen in diesem Plan setzen eine funktionierende Führungsstruktur voraus. Ohne sie bleiben operative Verbesserungen punktuell. Führung ist kein nice-to-have — sie ist die Infrastruktur für alle anderen Hebel.',
      kpiContext: productiveReps > 0 ? `${productiveReps} Reps aktiv | Monatsziel je Rep: ${fmtE(targetPerRepMonth)} | Projizierte Ziellücke: ${projectedGap > 0 ? fmtE(projectedGap) : 'n/a'}` : '',
      measures: measures4.slice(0, 5),
      successCriteria: [
        { metric: 'Führungsrhythmus', target: 'Wöchentliche 1:1s und monatliche Team-Reviews sind fest im Kalender verankert' },
        { metric: 'Forecast-Qualität', target: 'Commits weichen nicht mehr als 20 % vom Ist ab — Genauigkeit > 80 %' },
        { metric: 'Rep-Transparenz', target: 'Jeder Rep kennt täglich seinen Gap und seinen priorisierten nächsten Schritt' },
      ],
      whyNow: 'Dieser Hebel ist Voraussetzung für die Wirkung aller anderen. Ohne Führungsrückhalt scheitern operative Maßnahmen in der Umsetzung — unabhängig davon, wie gut sie konzipiert sind.',
      impactStatement: 'Skalierbarkeit, Vorhersagbarkeit und Verlässlichkeit des gesamten Vertriebssystems',
      firstStep: 'Diese Woche: Wöchentlichen 1:1-Slot je Rep im Kalender blockieren und Tracking-Sheet für Pacing und nächsten Schritt anlegen.',
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // EMPFEHLUNG 5 — Prozesse, System & Skalierbarkeit
  // Thema: CRM-Hygiene, Onboarding, Dokumentation, Sales Cycle
  // ═══════════════════════════════════════════════════════════════════════
  {
    const measures5: RecData['measures'] = [];
    measures5.push({
      title: hasPipeline ? 'Sofortiges Pipeline-Audit: Deals kategorisieren und bereinigen' : 'CRM-Hygiene als wöchentlichen Standard verankern',
      body: hasPipeline
        ? 'Alle aktiven Deals in drei Kategorien einteilen: (1) Abschlussreif — klarer Entscheider, definiertes Timing, nächster Schritt fest. (2) In Bearbeitung — qualifiziert, nächster Schritt offen. (3) Zu qualifizieren oder zu schließen — kein Entscheider, kein Timing, keine Aktivität seit >30 Tagen. Deals der Kategorie 3 reaktivieren oder aktiv schließen. Schlanke, saubere Pipeline schlägt volle, unstrukturierte Pipeline in jeder Kennzahl.'
        : `Jeden Freitag prüft jeder Rep seine Pipeline: Kein Deal ohne dokumentierten Next Step. Kein Deal älter als ${Math.round(salesCycleMonths * 2)} Monate ohne Reaktivierungsversuch oder aktive Schließung. Saubere Daten sind nicht Selbstzweck — sie sind die Voraussetzung für valide Forecasts, sinnvolles Coaching und messbare Steuerung.`,
      lever: hasPipeline ? 'Pipeline-Audit' : 'Datendisziplin',
    });
    measures5.push({
      title: salesCycleMonths > 3 ? `Sales-Cycle-Engpass identifizieren und um 30 % verkürzen` : 'Prozessgeschwindigkeit zwischen Discovery und Angebotsversand erhöhen',
      body: salesCycleMonths > 3
        ? `Der aktuelle Vertriebszyklus von ${salesCycleMonths} Monaten hat direkte Liquiditätswirkung${avgDealSize > 0 ? ` — bei ${fmtE(avgDealSize)} durchschnittlicher Deal-Größe bedeutet jede Cycle-Verkürzung direkten Cash-Flow-Gewinn` : ''}. Pilotprojekt: Welche eine Phase (Discovery-to-Proposal oder Proposal-to-Decision) dauert am längsten? Diesen Engpass um 30 % verkürzen. Das entspricht einem zusätzlichen Abschluss pro Quartal aus dem bestehenden Pipeline-Volumen — ohne einen neuen Lead.`
        : 'Zwischen Discovery-Gespräch und Angebotsversand sollten nicht mehr als 5 Arbeitstage liegen. Jede Woche Verzögerung reduziert die Abschlusswahrscheinlichkeit messbar. Interne Freigabeprozesse, Angebotserstellung und Pricing-Genehmigungen standardisieren.',
      lever: salesCycleMonths > 3 ? 'Cycle-Optimierung' : 'Prozessgeschwindigkeit',
    });
    measures5.push({
      title: 'Strukturiertes Onboarding — erster Deal innerhalb von 60 Tagen',
      body: 'Klar definiertes Onboarding-Programm: Woche 1–2 Produkt & Markt, Woche 3–4 Discovery-Shadowing, Woche 5–6 eigene Gespräche mit Live-Coaching, Woche 7–8 erster Deal-Versuch mit strukturierter Begleitung. Wie lange dauert es aktuell bis zum ersten Abschluss eines neuen Reps? Jede Woche früher bedeutet direkt zusätzlichen Umsatz — und ein schnellerer ROI auf Recruiting-Kosten.',
      lever: 'Kapazitätsaufbau',
    });
    measures5.push({
      title: 'Playbook und Best-Practice-Dokumentation systematisch aufbauen',
      body: 'Was im Team funktioniert, wird schriftlich festgehalten: Angebotsvorlagen für verschiedene Kundentypen, bewährte Formulierungen für Einwandbehandlung und Closing, strukturierter Discovery-Fragenkatalog, erfolgreiche Case Studies für Präsentationen. Wissenstransfer darf nicht von der Anwesenheit einzelner Personen abhängen. Ein Playbook ist die Grundlage für Skalierbarkeit — und der Schutz gegen Wissensabgang bei Fluktuation.',
      lever: 'Wissensinfrastruktur',
    });
    measures5.push({
      title: 'Vertriebssystem auf Personenunabhängigkeit und Skalierbarkeit ausrichten',
      body: 'Prozesse, Standards, Entscheidungsregeln und Eskalationswege müssen dokumentiert und testbar sein — unabhängig von einzelnen Top-Performern. Was nicht im System ist, verlässt das Unternehmen mit der nächsten Kündigung. Jährliche Überprüfung: Welche Prozesse hängen noch von Einzelpersonen ab? Diese Abhängigkeiten systematisch eliminieren.',
      lever: 'Systemunabhängigkeit',
    });

    recs.push({
      rank: 5,
      category: 'Prozess & Skalierbarkeit',
      title: 'Vertriebssystem — Prozesse und Skalierbarkeit',
      urgencyLabel: 'Wirkungshorizont',
      urgencyValue: 'Mittel- bis langfristig',
      timeframe: '30 – 90 Tage',
      timeframePhase: 'Phase 2–3',
      headline: 'Prozesse, die unabhängig von Einzelpersonen funktionieren — skalierbar und robust.',
      situationText: `Das Vertriebssystem ist in weiten Teilen personenabhängig. ${hasPipeline ? 'Die Pipeline enthält Deals ohne klare Entscheidungsstruktur oder Aktivitäten — ein Forecast-Risiko.' : 'Wenn ein Top-Performer ausfällt oder das Team wächst, bricht Umsatz ein.'} Nicht weil die Strategie falsch ist, sondern weil Wissen und Prozesse nicht im System verankert sind.`,
      rootCauseText: 'Fehlende Standardisierung in Follow-up, Angebotserstellung, Onboarding und CRM-Nutzung führt dazu, dass jeder Rep das Rad neu erfindet. Was beim einen funktioniert, bleibt beim anderen ungenutzt — das Gegenteil von Skalierbarkeit.',
      consequenceText: `Ein Vertrieb ohne standardisierte Prozesse hat eine natürliche Wachstumsgrenze — die Kapazität und Qualität der einzelnen Person.${salesCycleMonths > 3 ? ` Kombiniert mit einem ${salesCycleMonths}-monatigen Sales Cycle entstehen Liquiditätslücken, die durch Prozessoptimierung signifikant reduzierbar sind.` : ''}`,
      kpiContext: `Sales Cycle: ${salesCycleMonths} Monate | Ø Deal: ${avgDealSize > 0 ? fmtE(avgDealSize) : 'n/a'}${hasPipeline ? ' | ⚠ Pipeline-Qualität kritisch' : ''}`,
      measures: measures5.slice(0, 5),
      successCriteria: [
        { metric: 'CRM-Hygiene', target: '> 90 % aller Deals haben dokumentierten Next Step und aktuelles Entscheidungsdatum' },
        { metric: 'Onboarding-Speed', target: 'Erster Deal neuer Reps innerhalb von 60 Tagen nach Start' },
        { metric: 'Playbook', target: 'Vollständige Dokumentation: Angebotsvorlagen, Einwandantworten, Discovery-Leitfaden' },
        { metric: 'Prozess-Unabhängigkeit', target: 'Kein kritischer Prozess hängt von einer einzelnen Person ab' },
      ],
      whyNow: 'System-Aufbau beginnt parallel zu Phase 1 und entfaltet seine volle Wirkung in 60–90 Tagen. Je länger gewartet wird, desto tiefer die Personenabhängigkeit und desto schwerer der Umstieg bei Wachstum oder Fluktuation.',
      impactStatement: `Skalierbarkeit und Robustheit des gesamten Vertriebs — unabhängig von Personalveränderungen${avgDealSize > 0 && salesCycleMonths > 3 ? ' und mit direkter Liquiditätswirkung durch kürzere Sales Cycles' : ''}`,
      firstStep: 'Nächste Woche: CRM-Pflichtfelder „Next Step" und „Entscheidungsdatum" aktivieren. In 2 Wochen: Pipeline-Audit der ältesten 20 % der Deals.',
    });
  }

  return recs;
}

function Top5ActionRecommendations(props: Top5Props) {
  const recs = buildFullRecs(props);
  if (recs.length === 0) return null;

  return (
    <section className="mb-16">
      {/* ── SECTION HEADER ─────────────────────────────────────────────── */}
      <div className="mb-12">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 border border-slate-700 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white">
            <Target className="h-3.5 w-3.5 text-emerald-400" />
            Detaillierte Handlungsempfehlungen
          </div>
          {props.aiStructuredOutput ? (
            <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
              <Sparkles className="h-3 w-3" />
              KI-generiert auf Basis Ihrer Daten
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-500">
              <Activity className="h-3 w-3" />
              Datenbasiert — B2B-Benchmarks
            </div>
          )}
        </div>
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight">
          Top 5 Empfehlungen.<br />
          <span className="text-slate-400">Konkret. Priorisiert. Umsetzbar.</span>
        </h2>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-3xl">
          Jede Empfehlung basiert auf Ihren Audit-Daten und dem Benchmarkvergleich. Sie enthält eine vollständige Situationsanalyse, die Ursache hinter dem Problem, fünf konkrete Maßnahmen mit Erklärungstiefe, messbare Erfolgskriterien und den empfohlenen ersten Schritt — heute.
        </p>
      </div>

      {/* ── RECOMMENDATION CARDS ───────────────────────────────────────── */}
      <div className="space-y-12">
        {recs.map((rec, i) => {
          const c = RC[i] ?? RC[0];
          const isTop = i === 0;
          return (
            <div
              key={rec.rank}
              className={`rounded-3xl overflow-hidden shadow-xl border ${c.border} ${isTop ? `ring-4 ${c.ring} ring-offset-4` : ''}`}
            >
              {/* ── CARD HEADER ──────────────────────────────────────────── */}
              <div className={`relative overflow-hidden bg-gradient-to-br ${c.grad} px-8 py-8 md:px-12 md:py-10`}>
                {/* Background decoration */}
                <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/5" />
                <div className="pointer-events-none absolute right-8 bottom-4 h-32 w-32 rounded-full bg-white/3" />

                <div className="relative flex items-start gap-6">
                  {/* Rank badge */}
                  <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-2xl bg-white/15 ring-2 ring-white/25 backdrop-blur-sm">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">Nr.</span>
                    <span className="text-2xl font-black text-white leading-none">{rec.rank}</span>
                  </div>

                  {/* Title block */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className={`rounded-full border border-white/20 bg-white/15 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-white`}>
                        {rec.category}
                      </span>
                      <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-semibold text-white/70">
                        {rec.timeframePhase} · {rec.timeframe}
                      </span>
                      {isTop && (
                        <span className="rounded-full bg-yellow-400 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-yellow-900">
                          Startpunkt
                        </span>
                      )}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-black text-white leading-snug">{rec.title}</h3>
                    {rec.headline !== rec.title && (
                      <p className="mt-2 text-base text-white/70 leading-relaxed font-medium">{rec.headline}</p>
                    )}
                  </div>

                  {/* Urgency chip */}
                  <div className="shrink-0 text-right hidden md:block">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1">{rec.urgencyLabel}</div>
                    <div className="text-2xl font-black text-white">{rec.urgencyValue}</div>
                  </div>
                </div>

                {/* KPI context bar */}
                {rec.kpiContext && (
                  <div className="relative mt-6 flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-5 py-3 backdrop-blur-sm">
                    <TrendingUp className="h-4 w-4 text-white/60 shrink-0" />
                    <span className="text-sm font-semibold text-white/80">{rec.kpiContext}</span>
                  </div>
                )}
              </div>

              {/* ── SITUATION / DIAGNOSE ─────────────────────────────────── */}
              <div className={`${c.accentBg} border-b ${c.border} px-8 py-7 md:px-12`}>
                <div className="grid gap-6 md:grid-cols-3">
                  <div>
                    <div className={`flex items-center gap-1.5 mb-3`}>
                      <div className={`h-2 w-2 rounded-full ${c.dot}`} />
                      <span className={`text-[10px] font-black uppercase tracking-wider ${c.label}`}>Ausgangssituation</span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{rec.situationText}</p>
                  </div>
                  <div>
                    <div className={`flex items-center gap-1.5 mb-3`}>
                      <div className={`h-2 w-2 rounded-full ${c.dot}`} />
                      <span className={`text-[10px] font-black uppercase tracking-wider ${c.label}`}>Ursache</span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{rec.rootCauseText}</p>
                  </div>
                  <div>
                    <div className={`flex items-center gap-1.5 mb-3`}>
                      <div className={`h-2 w-2 rounded-full ${c.dot}`} />
                      <span className={`text-[10px] font-black uppercase tracking-wider ${c.label}`}>Konsequenz</span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{rec.consequenceText}</p>
                  </div>
                </div>
              </div>

              {/* ── 5 MASSNAHMEN ─────────────────────────────────────────── */}
              <div className="bg-white px-8 py-8 md:px-12">
                <div className={`flex items-center gap-2 mb-7`}>
                  <div className={`h-px flex-1 ${c.accentBg} border-t ${c.accentBorder}`} />
                  <span className={`text-[10px] font-black uppercase tracking-wider ${c.accent} px-3`}>
                    Die 5 konkreten Maßnahmen
                  </span>
                  <div className={`h-px flex-1 ${c.accentBg} border-t ${c.accentBorder}`} />
                </div>

                <div className="space-y-6">
                  {rec.measures.map((m, mi) => (
                    <div key={mi} className={`rounded-2xl border ${c.accentBorder} overflow-hidden`}>
                      {/* Measure header */}
                      <div className={`flex items-center gap-4 px-5 py-4 ${c.accentBg}`}>
                        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${c.badge}`}>
                          <span className="text-sm font-black text-white">{mi + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-black text-slate-900 leading-snug`}>{m.title}</p>
                        </div>
                        <span className={`shrink-0 rounded-full ${c.pill} px-2.5 py-0.5 text-[10px] font-bold`}>
                          {m.lever}
                        </span>
                      </div>
                      {/* Measure body */}
                      {m.body && (
                        <div className="px-5 py-4 bg-white">
                          <p className="text-sm text-slate-600 leading-relaxed pl-12">{m.body}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* ── ERFOLGSKRITERIEN ─────────────────────────────────────── */}
              <div className={`${c.accentBg} border-t border-b ${c.border} px-8 py-7 md:px-12`}>
                <div className={`flex items-center gap-1.5 mb-5`}>
                  <CheckCircle className={`h-4 w-4 ${c.accent}`} />
                  <span className={`text-[10px] font-black uppercase tracking-wider ${c.label}`}>Messbare Erfolgskriterien</span>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  {rec.successCriteria.map((sc, si) => (
                    <div key={si} className={`rounded-xl border ${c.border} bg-white px-4 py-3`}>
                      <div className={`text-[9px] font-black uppercase tracking-wider ${c.accent} mb-1.5`}>{sc.metric}</div>
                      <p className="text-sm text-slate-700 font-medium leading-snug">{sc.target}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── FOOTER: WHY NOW + IMPACT + FIRST STEP ────────────────── */}
              <div className="bg-white px-8 py-7 md:px-12">
                <div className="grid gap-6 md:grid-cols-3">
                  {/* Why now */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-3">
                      <AlertTriangle className={`h-3.5 w-3.5 ${c.accent}`} />
                      <span className={`text-[10px] font-black uppercase tracking-wider ${c.label}`}>Warum jetzt</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{rec.whyNow}</p>
                  </div>
                  {/* Impact */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-3">
                      <Zap className={`h-3.5 w-3.5 ${c.accent}`} />
                      <span className={`text-[10px] font-black uppercase tracking-wider ${c.label}`}>Erwarteter Impact</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{rec.impactStatement}</p>
                  </div>
                  {/* First step */}
                  <div className={`rounded-2xl border-2 ${c.border} ${c.accentBg} px-5 py-4`}>
                    <div className="flex items-center gap-1.5 mb-2">
                      <ArrowRight className={`h-3.5 w-3.5 ${c.accent}`} />
                      <span className={`text-[10px] font-black uppercase tracking-wider ${c.label}`}>Erster Schritt heute</span>
                    </div>
                    <p className={`text-sm font-semibold text-slate-800 leading-relaxed`}>{rec.firstStep}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── CLOSING NOTE ───────────────────────────────────────────────── */}
      <div className="mt-10 rounded-3xl border border-slate-200 bg-slate-50 px-8 py-7 md:px-10">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-200">
            <ShieldAlert className="h-5 w-5 text-slate-500" />
          </div>
          <div>
            <p className="text-base font-bold text-slate-900 mb-2">Was dieser Plan nicht ersetzt</p>
            <p className="text-sm text-slate-600 leading-relaxed max-w-3xl">
              Die Empfehlungen in diesem Plan definieren Richtung, Prioritäten und Stoßrichtungen. Konkrete Playbooks, fertige Gesprächsleitfäden, CRM-Konfigurationen, Angebotsvorlagen und Schritt-für-Schritt-Prozesse sind Bestandteil der individuellen Umsetzungsbegleitung — nicht dieses digitalen Plans. Der Unterschied zwischen einem guten Plan und messbarem Ergebnis ist konsequente Umsetzung mit erfahrenem Rückhalt.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}